/* eslint-disable */
// @ts-nocheck
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useAuth } from '../../contexts/AuthContext.js';
import { commandCenterService } from '../../services/commandCenter.js';
import exexLogo from '../../assets/straightened_exex_gradient.png';
import './date-picker.css';

// ═══════════════════════════════
// ── CONFIG & CONSTANTS ──
// ═══════════════════════════════

const STAGES = [
  {
    key: 'identified',
    label: 'Identified',
    color: '#4a5568',
    idx: 0,
    prob: 0.05,
    benchmarkDays: 30,
  },
  { key: 'outreach', label: 'Outreach', color: '#6366f1', idx: 1, prob: 0.1, benchmarkDays: 21 },
  { key: 'discovery', label: 'Discovery', color: '#8b5cf6', idx: 2, prob: 0.15, benchmarkDays: 28 },
  { key: 'demo', label: 'Demo', color: '#06b6d4', idx: 3, prob: 0.25, benchmarkDays: 21 },
  { key: 'champion', label: 'Champion', color: '#0ea5e9', idx: 4, prob: 0.4, benchmarkDays: 30 },
  { key: 'it_review', label: 'IT Review', color: '#f59e0b', idx: 5, prob: 0.5, benchmarkDays: 45 },
  { key: 'proposal', label: 'Proposal', color: '#f97316', idx: 6, prob: 0.65, benchmarkDays: 21 },
  { key: 'legal', label: 'Legal/Contract', color: '#ef4444', idx: 7, prob: 0.8, benchmarkDays: 45 },
  { key: 'signed', label: 'Signed', color: '#10b981', idx: 8, prob: 0.95, benchmarkDays: 14 },
  {
    key: 'onboarding',
    label: 'Onboarding',
    color: '#14b8a6',
    idx: 9,
    prob: 0.98,
    benchmarkDays: 60,
  },
  {
    key: 'commercial',
    label: 'Commercial ✦',
    color: '#00ffc8',
    idx: 10,
    prob: 1.0,
    benchmarkDays: 0,
  },
];
const STAGE_MAP = Object.fromEntries(STAGES.map((s) => [s.key, s]));

// Deal status (separate from pipeline stage)
const DEAL_STATUSES = [
  { key: 'active', label: 'Active', color: '#06b6d4' },
  { key: 'on_hold', label: 'On Hold', color: '#f59e0b' },
  { key: 'lost', label: 'Lost', color: '#ef4444' },
];

// Team members are fetched from the API
const LEAD_COLORS = [
  '#00ffc8',
  '#06b6d4',
  '#8b5cf6',
  '#f59e0b',
  '#ef4444',
  '#10b981',
  '#f97316',
  '#ec4899',
  '#6366f1',
  '#14b8a6',
];

const ADVOCATE_STATUSES = [
  { key: 'interested', label: 'Interested', color: '#64748b', icon: '○' },
  { key: 'active', label: 'Active', color: '#06b6d4', icon: '◑' },
  { key: 'champion', label: 'Champion', color: '#f59e0b', icon: '★' },
  { key: 'evangelist', label: 'Evangelist', color: '#00ffc8', icon: '✦' },
];
const ADVOCATE_STATUS_MAP = Object.fromEntries(ADVOCATE_STATUSES.map((s) => [s.key, s]));

// Network TAM is fetched from the API

const todayStr = () => new Date().toISOString().split('T')[0];
const daysSince = (d) => {
  if (!d) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor((today.getTime() - new Date(d).getTime()) / 86400000);
};

// Map API team member to internal format
function mapTeamMember(u, index) {
  const name = `${u.firstName} ${u.lastName}`;
  const initials = name
    .trim()
    .split(/\s+/)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('')
    .slice(0, 2);
  const color = LEAD_COLORS[index % LEAD_COLORS.length];
  return { id: u.id, name, initials, color, email: u.email, role: u.role, roles: u.roles };
}

// Map API facility to internal format
function mapApiFacility(f) {
  const milestones = {};
  for (const m of f.milestones || []) {
    milestones[m.stageKey] = m.milestoneDate?.split('T')[0] || m.milestoneDate;
  }
  const activityLog = (f.activityLog || []).map((a) => ({
    id: a.id,
    date: a.activityDate?.split('T')[0] || a.activityDate,
    who: a.userId,
    userName: a.user ? `${a.user.firstName} ${a.user.lastName}` : undefined,
    text: a.activityText,
  }));
  const advocates = (f.advocates || []).map((a) => ({
    id: a.id,
    name: a.name,
    specialty: a.specialty,
    email: a.email || '',
    cell: a.cell || '',
    status: a.status,
  }));
  return {
    id: f.id,
    name: f.name,
    networkId: f.networkId || null,
    network: f.network?.name || '',
    city: f.city,
    state: f.state,
    country: f.country,
    ors: f.ors,
    surgeons: f.surgeons,
    stage: f.stage,
    dealValue: f.dealValue,
    dealStatus: f.dealStatus || 'active',
    lead: f.leadId || '',
    champion: f.champion || '',
    economicBuyer: f.economicBuyer || '',
    technicalBuyer: f.technicalBuyer || '',
    competitor: f.competitor || '',
    projectedStart: f.projectedStart?.split('T')[0] || '',
    nextAction: f.nextAction || '',
    nextActionDate: f.nextActionDate?.split('T')[0] || '',
    lastActivity: f.lastActivity?.split('T')[0] || '',
    notes: f.notes || '',
    compliance: f.compliance || '',
    milestones,
    advocates,
    activityLog,
  };
}
const formatCurrency = (v) =>
  v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(0)}K` : `$${v}`;
const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};
const getHealthColor = (h) =>
  h >= 80 ? '#00ffc8' : h >= 60 ? '#f59e0b' : h >= 40 ? '#f97316' : '#ef4444';
const getCountryFlag = (c) => ({ US: '🇺🇸', UK: '🇬🇧', IE: '🇮🇪' })[c] || '🌐';

// Dynamic health: factor in days stale in current stage
function calcDynamicHealth(facility) {
  const stage = STAGE_MAP[facility.stage];
  if (!stage || facility.stage === 'commercial') return facility.health || 100;
  const lastMilestone = facility.milestones[facility.stage];
  const daysInStage = lastMilestone ? daysSince(lastMilestone) : 0;
  const benchmark = stage.benchmarkDays;
  const staleRatio = benchmark > 0 ? daysInStage / benchmark : 0;
  let base = 40 + stage.idx * 6;
  if (staleRatio > 2.0) base -= 35;
  else if (staleRatio > 1.5) base -= 25;
  else if (staleRatio > 1.0) base -= 12;
  else base += 10;
  return Math.max(10, Math.min(100, base));
}

function getVelocityStatus(facility) {
  const stage = STAGE_MAP[facility.stage];
  if (!stage || facility.stage === 'commercial')
    return { label: 'Live', color: '#00ffc8', days: 0, ratio: 0 };
  if (facility.dealStatus === 'on_hold')
    return { label: 'On Hold', color: '#f59e0b', days: 0, ratio: 0 };
  if (facility.dealStatus === 'lost') return { label: 'Lost', color: '#ef4444', days: 0, ratio: 0 };
  const lastMilestone = facility.milestones[facility.stage];
  const days = lastMilestone ? daysSince(lastMilestone) : 0;
  const ratio = stage.benchmarkDays > 0 ? days / stage.benchmarkDays : 0;
  if (ratio > 2.0) return { label: `${days}d — Critical`, color: '#ef4444', days, ratio };
  if (ratio > 1.5) return { label: `${days}d — Stale`, color: '#f97316', days, ratio };
  if (ratio > 1.0) return { label: `${days}d — Aging`, color: '#f59e0b', days, ratio };
  return { label: `${days}d`, color: '#64748b', days, ratio };
}

// Facilities, team, and network TAM are fetched from the API
// (sample data removed)

// ═══════════════════════════════
// ── DATE PICKER ──
// ═══════════════════════════════

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function DatePicker({
  value,
  onChange,
  placeholder = 'Select date...',
  compact = false,
  overdue = false,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const parsed = value ? new Date(value + 'T00:00:00') : null;
  const [viewYear, setViewYear] = useState(parsed?.getFullYear() ?? new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(parsed?.getMonth() ?? new Date().getMonth());
  const triggerRef = useRef(null);

  useEffect(() => {
    if (open && parsed) {
      setViewYear(parsed.getFullYear());
      setViewMonth(parsed.getMonth());
    }
  }, [open]);

  const today = new Date();
  const todayDateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const days = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1);
    const startDay = first.getDay();
    const result = [];
    for (let i = startDay - 1; i >= 0; i--) {
      const d = new Date(viewYear, viewMonth, -i);
      result.push({ date: d, otherMonth: true });
    }
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    for (let i = 1; i <= daysInMonth; i++) {
      result.push({ date: new Date(viewYear, viewMonth, i), otherMonth: false });
    }
    const remaining = 42 - result.length;
    for (let i = 1; i <= remaining; i++) {
      result.push({ date: new Date(viewYear, viewMonth + 1, i), otherMonth: true });
    }
    return result;
  }, [viewYear, viewMonth]);

  const toStr = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const handleSelect = (d) => {
    onChange(toStr(d));
    setOpen(false);
  };

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else setViewMonth(viewMonth + 1);
  };

  const displayValue = parsed
    ? parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  const triggerClass = [
    'cc-datepicker__trigger',
    compact && 'cc-datepicker__trigger--compact',
    !displayValue && 'cc-datepicker__trigger--placeholder',
    overdue && 'cc-datepicker__trigger--overdue',
  ]
    .filter(Boolean)
    .join(' ');

  const getDropdownPos = () => {
    if (!triggerRef.current) return { top: 0, left: 0 };
    const rect = triggerRef.current.getBoundingClientRect();
    const dropdownHeight = 360; // approximate height of the calendar dropdown
    const spaceBelow = window.innerHeight - rect.bottom;
    const flipUp = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
    return flipUp
      ? { bottom: window.innerHeight - rect.top + 4, left: rect.left }
      : { top: rect.bottom + 4, left: rect.left };
  };

  if (disabled) {
    return (
      <span
        className={triggerClass}
        style={{ cursor: 'default', opacity: 0.6, display: 'inline-flex' }}
      >
        {displayValue || '—'}
      </span>
    );
  }

  return (
    <div className="cc-datepicker">
      <button
        ref={triggerRef}
        className={triggerClass}
        onClick={() => setOpen(!open)}
        type="button"
      >
        <span>{displayValue || placeholder}</span>
        <svg
          className="cc-datepicker__icon"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </button>
      {open &&
        createPortal(
          <>
            <div className="cc-datepicker__backdrop" onClick={() => setOpen(false)} />
            <div className="cc-datepicker__dropdown" style={getDropdownPos()}>
              <div className="cc-datepicker__header">
                <span className="cc-datepicker__month-label">
                  {MONTH_NAMES[viewMonth]} {viewYear}
                </span>
                <div className="cc-datepicker__nav">
                  <button className="cc-datepicker__nav-btn" onClick={prevMonth} type="button">
                    ‹
                  </button>
                  <button className="cc-datepicker__nav-btn" onClick={nextMonth} type="button">
                    ›
                  </button>
                </div>
              </div>
              <div className="cc-datepicker__weekdays">
                {WEEKDAYS.map((w) => (
                  <div key={w} className="cc-datepicker__weekday">
                    {w}
                  </div>
                ))}
              </div>
              <div className="cc-datepicker__days">
                {days.map(({ date: d, otherMonth }, i) => {
                  const ds = toStr(d);
                  const cls = [
                    'cc-datepicker__day',
                    otherMonth && 'cc-datepicker__day--other-month',
                    ds === todayDateStr && 'cc-datepicker__day--today',
                    ds === value && 'cc-datepicker__day--selected',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <button key={i} className={cls} onClick={() => handleSelect(d)} type="button">
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
              <div className="cc-datepicker__footer">
                <button
                  className="cc-datepicker__today-btn"
                  onClick={() => handleSelect(today)}
                  type="button"
                >
                  Today
                </button>
                <button
                  className="cc-datepicker__clear-btn"
                  onClick={() => {
                    onChange('');
                    setOpen(false);
                  }}
                  type="button"
                >
                  Clear
                </button>
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
}

// ═══════════════════════════════
// ── MICRO COMPONENTS ──
// ═══════════════════════════════

const inputStyle = {
  background: '#080d14',
  border: '1px solid #1e2d3d',
  borderRadius: 6,
  color: '#e2e8f0',
  padding: '10px 14px',
  fontSize: 13,
  width: '100%',
  outline: 'none',
  fontFamily: 'inherit',
};
const labelStyle = {
  fontSize: 11,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  fontWeight: 600,
  marginBottom: 6,
  display: 'block',
};

function HealthIndicator({ value }) {
  const c = getHealthColor(value);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: c,
          boxShadow: `0 0 6px ${c}60`,
        }}
      />
      <span style={{ color: c, fontSize: 13, fontWeight: 600 }}>{value}</span>
    </div>
  );
}

function ProgressBar({ stage }) {
  const s = STAGE_MAP[stage];
  const p = ((s.idx + 1) / STAGES.length) * 100;
  return (
    <div style={{ width: '100%' }}>
      <div style={{ width: '100%', height: 4, borderRadius: 2, background: '#1a2332' }}>
        <div
          style={{
            width: `${p}%`,
            height: '100%',
            borderRadius: 2,
            background: `linear-gradient(90deg,${s.color}90,${s.color})`,
            transition: 'width 0.6s',
            boxShadow: `0 0 8px ${s.color}40`,
          }}
        />
      </div>
    </div>
  );
}

function StageBadge({ stage }) {
  const s = STAGE_MAP[stage];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        color: s.color,
        background: `${s.color}18`,
        border: `1px solid ${s.color}35`,
      }}
    >
      {s.label}
    </span>
  );
}

function DealStatusBadge({ status }) {
  if (status === 'active' || !status) return null;
  const s = DEAL_STATUSES.find((d) => d.key === status);
  if (!s) return null;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        color: s.color,
        background: `${s.color}18`,
        border: `1px solid ${s.color}35`,
      }}
    >
      {s.label}
    </span>
  );
}

function VelocityBadge({ facility }) {
  const v = getVelocityStatus(facility);
  if (facility.stage === 'commercial') return null;
  return <span style={{ fontSize: 10, color: v.color, fontWeight: 600 }}>{v.label}</span>;
}

function TeamBadge({ memberId, team }) {
  const m = team.find((t) => t.id === memberId);
  if (!m) return <span style={{ fontSize: 12, color: '#475569' }}>—</span>;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div
        style={{
          width: 26,
          height: 26,
          borderRadius: '50%',
          background: `${m.color}25`,
          border: `1.5px solid ${m.color}60`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 10,
          fontWeight: 700,
          color: m.color,
        }}
      >
        {m.initials}
      </div>
      <span style={{ fontSize: 12, color: '#94a3b8' }}>{m.name.split(' ').pop()}</span>
    </div>
  );
}

function LeadPicker({ currentLeadId, team, canEdit, onSelect }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = team.find((t) => t.id === currentLeadId);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div
        onClick={canEdit ? () => setOpen((o) => !o) : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          cursor: canEdit ? 'pointer' : 'default',
          padding: '2px 0',
          borderRadius: 4,
        }}
      >
        {current ? (
          <>
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                background: `${current.color}25`,
                border: `1.5px solid ${current.color}60`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                fontWeight: 700,
                color: current.color,
              }}
            >
              {current.initials}
            </div>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>{current.name.split(' ').pop()}</span>
          </>
        ) : (
          <span style={{ fontSize: 12, color: '#475569' }}>—</span>
        )}
        {canEdit && <span style={{ fontSize: 9, color: '#475569', marginLeft: 2 }}>▼</span>}
      </div>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 4,
            background: '#0d1520',
            border: '1px solid #1e2d3d',
            borderRadius: 6,
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            zIndex: 50,
            minWidth: 180,
            maxHeight: 240,
            overflowY: 'auto',
            padding: '4px 0',
          }}
        >
          <div
            onClick={() => {
              onSelect(null);
              setOpen(false);
            }}
            style={{
              padding: '8px 12px',
              fontSize: 12,
              color: '#475569',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: !currentLeadId ? '#1e2d3d40' : 'transparent',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#1e2d3d';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = !currentLeadId ? '#1e2d3d40' : 'transparent';
            }}
          >
            <span>—</span>
            <span>Unassigned</span>
          </div>
          {team.map((t) => (
            <div
              key={t.id}
              onClick={() => {
                onSelect(t.id);
                setOpen(false);
              }}
              style={{
                padding: '8px 12px',
                fontSize: 12,
                color: '#e2e8f0',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: t.id === currentLeadId ? '#1e2d3d40' : 'transparent',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#1e2d3d';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background =
                  t.id === currentLeadId ? '#1e2d3d40' : 'transparent';
              }}
            >
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: `${t.color}25`,
                  border: `1.5px solid ${t.color}60`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 9,
                  fontWeight: 700,
                  color: t.color,
                  flexShrink: 0,
                }}
              >
                {t.initials}
              </div>
              <span>{t.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EditableNumber({ value, onSave, canEdit }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const commit = () => {
    setEditing(false);
    const parsed = parseInt(draft, 10);
    if (!isNaN(parsed) && parsed >= 0 && parsed !== value) onSave(parsed);
    else setDraft(String(value));
  };

  if (!canEdit) {
    return <div style={{ fontSize: 20, fontWeight: 700, color: '#e2e8f0' }}>{value}</div>;
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        min={0}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') {
            setDraft(String(value));
            setEditing(false);
          }
        }}
        style={{
          width: 60,
          fontSize: 20,
          fontWeight: 700,
          color: '#e2e8f0',
          background: '#0d1520',
          border: '1px solid #06b6d450',
          borderRadius: 4,
          padding: '2px 6px',
          outline: 'none',
        }}
      />
    );
  }

  return (
    <div
      onClick={() => {
        setDraft(String(value));
        setEditing(true);
      }}
      style={{
        fontSize: 20,
        fontWeight: 700,
        color: '#e2e8f0',
        cursor: 'pointer',
        borderRadius: 4,
        padding: '2px 4px',
        margin: '-2px -4px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.background = '#ffffff06';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      {value}
      <span style={{ fontSize: 9, color: '#475569' }}>✎</span>
    </div>
  );
}

function MetricCard({ label, value, sub, accent = '#00ffc8' }) {
  return (
    <div
      style={{
        background: 'linear-gradient(135deg,#0d1520,#111d2e)',
        border: '1px solid #1e2d3d',
        borderRadius: 8,
        padding: '16px 20px',
        flex: 1,
        minWidth: 150,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: '#64748b',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          marginBottom: 6,
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: accent, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function FormField({ label, children, span = 1 }) {
  return (
    <div style={{ gridColumn: `span ${span}` }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

function Modal({ title, onClose, width = 560, children }) {
  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          zIndex: 2000,
          backdropFilter: 'blur(4px)',
        }}
      />
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%,-50%)',
          width,
          maxWidth: '95vw',
          maxHeight: '90vh',
          overflowY: 'auto',
          background: 'linear-gradient(180deg,#0d1520,#111d2e)',
          border: '1px solid #1e2d3d',
          borderRadius: 12,
          zIndex: 2001,
          boxShadow: '0 25px 80px rgba(0,0,0,0.6)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 28px',
            borderBottom: '1px solid #1e2d3d',
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 700, color: '#e2e8f0' }}>{title}</div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: '1px solid #1e2d3d',
              borderRadius: 4,
              color: '#64748b',
              cursor: 'pointer',
              padding: '4px 10px',
              fontSize: 12,
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ padding: '24px 28px' }}>{children}</div>
      </div>
    </>
  );
}

// ── INLINE EDITABLE FIELDS ──
function EditableField({ value, onSave, placeholder = '', disabled = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const startEdit = () => {
    if (disabled) return;
    setDraft(value);
    setEditing(true);
  };
  const save = () => {
    onSave(draft);
    setEditing(false);
  };
  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };
  if (editing)
    return (
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          style={{ ...inputStyle, padding: '6px 10px', fontSize: 12, flex: 1 }}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') cancel();
          }}
          autoFocus
          placeholder={placeholder}
        />
        <button
          onClick={save}
          style={{
            background: '#06b6d415',
            border: '1px solid #06b6d435',
            borderRadius: 4,
            color: '#06b6d4',
            padding: '4px 10px',
            fontSize: 10,
            fontWeight: 700,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          Save
        </button>
        <button
          onClick={cancel}
          style={{
            background: 'none',
            border: '1px solid #1e2d3d',
            borderRadius: 4,
            color: '#64748b',
            padding: '4px 8px',
            fontSize: 10,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          ✕
        </button>
      </div>
    );
  return (
    <div
      onClick={startEdit}
      style={{
        cursor: disabled ? 'default' : 'pointer',
        borderRadius: 4,
        padding: '2px 4px',
        margin: '-2px -4px',
        transition: 'background 0.15s',
        position: 'relative',
      }}
      onMouseOver={(e) => !disabled && (e.currentTarget.style.background = '#ffffff06')}
      onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <span style={{ fontSize: 12, color: value?.includes('TBD') ? '#475569' : '#cbd5e1' }}>
        {value || <span style={{ color: '#334155' }}>{disabled ? '—' : 'Click to add...'}</span>}
      </span>
      {!disabled && (
        <span
          style={{
            position: 'absolute',
            right: 4,
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: 10,
            color: '#334155',
            opacity: 0.5,
          }}
        >
          ✎
        </span>
      )}
    </div>
  );
}

function EditableTextarea({ value, onSave, placeholder = '', disabled = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const startEdit = () => {
    if (disabled) return;
    setDraft(value);
    setEditing(true);
  };
  const save = () => {
    onSave(draft);
    setEditing(false);
  };
  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };
  if (editing)
    return (
      <div>
        <textarea
          style={{
            ...inputStyle,
            padding: '8px 10px',
            fontSize: 12,
            minHeight: 60,
            resize: 'vertical',
            width: '100%',
          }}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          autoFocus
          placeholder={placeholder}
        />
        <div style={{ display: 'flex', gap: 6, marginTop: 6, justifyContent: 'flex-end' }}>
          <button
            onClick={cancel}
            style={{
              background: 'none',
              border: '1px solid #1e2d3d',
              borderRadius: 4,
              color: '#64748b',
              padding: '4px 10px',
              fontSize: 10,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={save}
            style={{
              background: '#06b6d415',
              border: '1px solid #06b6d435',
              borderRadius: 4,
              color: '#06b6d4',
              padding: '4px 12px',
              fontSize: 10,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Save
          </button>
        </div>
      </div>
    );
  return (
    <div
      onClick={startEdit}
      style={{
        cursor: disabled ? 'default' : 'pointer',
        borderRadius: 4,
        padding: '4px 6px',
        margin: '-4px -6px',
        transition: 'background 0.15s',
        position: 'relative',
      }}
      onMouseOver={(e) => !disabled && (e.currentTarget.style.background = '#ffffff06')}
      onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <span style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>
        {value || <span style={{ color: '#334155' }}>{disabled ? '—' : 'Click to add...'}</span>}
      </span>
      {!disabled && (
        <span
          style={{
            position: 'absolute',
            right: 6,
            top: 6,
            fontSize: 10,
            color: '#334155',
            opacity: 0.5,
          }}
        >
          ✎
        </span>
      )}
    </div>
  );
}

// ═══════════════════════════════
// ── MODALS ──
// ═══════════════════════════════

function AddFacilityModal({ onClose, onSave, team, networks = [], facility = null }) {
  const isEdit = !!facility;
  const [form, setForm] = useState({
    name: facility?.name || '',
    networkId: facility?.networkId || '',
    city: facility?.city || '',
    state: facility?.state || '',
    country: facility?.country || 'US',
    ors: facility ? String(facility.ors ?? '') : '',
    surgeons: facility ? String(facility.surgeons ?? '') : '',
    stage: facility?.stage || 'identified',
    dealValue: facility ? String(facility.dealValue ?? '') : '',
    lead: facility?.leadId || team[0]?.id || '',
    champion: facility?.champion || 'TBD',
    economicBuyer: facility?.economicBuyer || 'TBD',
    technicalBuyer: facility?.technicalBuyer || 'TBD',
    competitor: facility?.competitor || 'Unknown',
    projectedStart: facility?.projectedStart ? facility.projectedStart.slice(0, 10) : '',
    notes: facility?.notes || '',
    compliance: facility?.compliance || 'Not started',
    nextAction: facility?.nextAction || 'Initial research',
    nextActionDate: facility?.nextActionDate ? facility.nextActionDate.slice(0, 10) : '',
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const handleSave = async () => {
    if (!form.name.trim()) return;
    try {
      await onSave({
        name: form.name.trim(),
        networkId: form.networkId || undefined,
        city: form.city.trim(),
        state: form.state.trim(),
        country: form.country,
        ors: parseInt(form.ors) || 0,
        surgeons: parseInt(form.surgeons) || 0,
        stage: form.stage,
        dealValue: parseInt(form.dealValue) || 0,
        leadId: form.lead || undefined,
        champion: form.champion,
        economicBuyer: form.economicBuyer,
        technicalBuyer: form.technicalBuyer,
        competitor: form.competitor,
        projectedStart: form.projectedStart || undefined,
        notes: form.notes,
        compliance: form.compliance,
        nextAction: form.nextAction,
        nextActionDate: form.nextActionDate || undefined,
      });
      onClose();
    } catch (err) {
      console.error(`Failed to ${isEdit ? 'update' : 'create'} facility:`, err);
    }
  };
  return (
    <Modal title={isEdit ? 'Edit Facility' : 'Add New Facility'} onClose={onClose} width={640}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <FormField label="Facility Name" span={2}>
          <input
            style={inputStyle}
            placeholder="e.g. Mayo Clinic Jacksonville"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
          />
        </FormField>
        <FormField label="Hospital Network">
          <select
            style={{ ...inputStyle, cursor: 'pointer' }}
            value={form.networkId}
            onChange={(e) => set('networkId', e.target.value)}
          >
            <option value="">Select network...</option>
            {networks.map((n) => (
              <option key={n.id} value={n.id}>
                {n.name}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Country">
          <select
            style={{ ...inputStyle, cursor: 'pointer' }}
            value={form.country}
            onChange={(e) => set('country', e.target.value)}
          >
            <option value="US">🇺🇸 United States</option>
            <option value="UK">🇬🇧 United Kingdom</option>
          </select>
        </FormField>
        <FormField label="City">
          <input
            style={inputStyle}
            placeholder="e.g. Jacksonville"
            value={form.city}
            onChange={(e) => set('city', e.target.value)}
          />
        </FormField>
        <FormField label="State / Region">
          <input
            style={inputStyle}
            placeholder="e.g. FL or England"
            value={form.state}
            onChange={(e) => set('state', e.target.value)}
          />
        </FormField>
        <FormField label="# of ORs">
          <input
            style={inputStyle}
            type="number"
            placeholder="e.g. 24"
            value={form.ors}
            onChange={(e) => set('ors', e.target.value)}
          />
        </FormField>
        <FormField label="# of Surgeons">
          <input
            style={inputStyle}
            type="number"
            placeholder="e.g. 75"
            value={form.surgeons}
            onChange={(e) => set('surgeons', e.target.value)}
          />
        </FormField>
        <FormField label="Est. Annual Deal Value ($)">
          <input
            style={inputStyle}
            type="number"
            placeholder="e.g. 350000"
            value={form.dealValue}
            onChange={(e) => set('dealValue', e.target.value)}
          />
        </FormField>
        <FormField label={isEdit ? 'Stage' : 'Starting Stage'}>
          <select
            style={{ ...inputStyle, cursor: 'pointer' }}
            value={form.stage}
            onChange={(e) => set('stage', e.target.value)}
          >
            {STAGES.filter((s) => s.key !== 'commercial').map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Lead Owner">
          <select
            style={{ ...inputStyle, cursor: 'pointer' }}
            value={form.lead}
            onChange={(e) => set('lead', e.target.value)}
          >
            {team.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Projected Start Date">
          <DatePicker
            value={form.projectedStart}
            onChange={(v) => set('projectedStart', v)}
            placeholder="Select date..."
          />
        </FormField>
        <div
          style={{
            gridColumn: 'span 2',
            borderTop: '1px solid #1e2d3d',
            paddingTop: 16,
            marginTop: 4,
          }}
        >
          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 12 }}>
            KEY CONTACTS
          </div>
        </div>
        <FormField label="Champion">
          <input
            style={inputStyle}
            placeholder="e.g. Dr. Jane Smith, Chief of Surgery"
            value={form.champion}
            onChange={(e) => set('champion', e.target.value)}
          />
        </FormField>
        <FormField label="Economic Buyer">
          <input
            style={inputStyle}
            placeholder="e.g. John Doe, CFO"
            value={form.economicBuyer}
            onChange={(e) => set('economicBuyer', e.target.value)}
          />
        </FormField>
        <FormField label="Technical Buyer">
          <input
            style={inputStyle}
            placeholder="e.g. IT Director"
            value={form.technicalBuyer}
            onChange={(e) => set('technicalBuyer', e.target.value)}
          />
        </FormField>
        <FormField label="Competitor">
          <input
            style={inputStyle}
            placeholder="e.g. Caresyntax"
            value={form.competitor}
            onChange={(e) => set('competitor', e.target.value)}
          />
        </FormField>
        <FormField label="Notes" span={2}>
          <textarea
            style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }}
            placeholder="Strategic context..."
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
          />
        </FormField>
        <FormField label="Next Action">
          <input
            style={inputStyle}
            value={form.nextAction}
            onChange={(e) => set('nextAction', e.target.value)}
          />
        </FormField>
        <FormField label="Next Action Due">
          <DatePicker
            value={form.nextActionDate}
            onChange={(v) => set('nextActionDate', v)}
            placeholder="Select date..."
          />
        </FormField>
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
        <button
          onClick={onClose}
          style={{
            padding: '10px 20px',
            borderRadius: 6,
            border: '1px solid #1e2d3d',
            background: 'transparent',
            color: '#94a3b8',
            fontSize: 13,
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={!form.name.trim()}
          style={{
            padding: '10px 28px',
            borderRadius: 6,
            border: '1px solid #00ffc840',
            background: form.name.trim()
              ? 'linear-gradient(135deg,#00ffc820,#00ffc810)'
              : '#1e2d3d',
            color: form.name.trim() ? '#00ffc8' : '#475569',
            fontSize: 13,
            cursor: form.name.trim() ? 'pointer' : 'default',
            fontWeight: 700,
            letterSpacing: '0.5px',
          }}
        >
          {isEdit ? 'Save Changes' : 'Add Facility'}
        </button>
      </div>
    </Modal>
  );
}

function FlipToCommercialModal({ facility, onClose, onConfirm }) {
  const [startDate, setStartDate] = useState(facility.projectedStart || todayStr());
  return (
    <Modal title="✦ Flip to Commercial" onClose={onClose} width={480}>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#e2e8f0', marginBottom: 4 }}>
          {facility.name}
        </div>
        <div style={{ fontSize: 13, color: '#64748b' }}>
          {facility.network} · {getCountryFlag(facility.country)} {facility.city}, {facility.state}
        </div>
      </div>
      <div
        style={{
          background: '#080d14',
          border: '1px solid #1e2d3d',
          borderRadius: 8,
          padding: 20,
          marginBottom: 20,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <span style={{ fontSize: 12, color: '#64748b' }}>Current Stage</span>
          <StageBadge stage={facility.stage} />
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '12px 0',
          }}
        >
          <div style={{ fontSize: 24, color: '#1e2d3d' }}>↓</div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#64748b' }}>New Stage</span>
          <span
            style={{
              display: 'inline-block',
              padding: '3px 10px',
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              color: '#00ffc8',
              background: '#00ffc818',
              border: '1px solid #00ffc835',
              boxShadow: '0 0 12px #00ffc815',
            }}
          >
            Commercial ✦
          </span>
        </div>
      </div>
      <FormField label="Commercial Start Date">
        <DatePicker
          value={startDate}
          onChange={(v) => setStartDate(v)}
          placeholder="Select start date..."
        />
      </FormField>
      <div
        style={{
          background: '#f59e0b10',
          border: '1px solid #f59e0b30',
          borderRadius: 8,
          padding: 16,
          marginTop: 16,
        }}
      >
        <div style={{ fontSize: 12, color: '#f59e0b', fontWeight: 600, marginBottom: 4 }}>
          ⚠ This marks the facility as commercially live
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.5 }}>
          All milestones preserved. Updates Contracted ARR.
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
        <button
          onClick={onClose}
          style={{
            padding: '10px 20px',
            borderRadius: 6,
            border: '1px solid #1e2d3d',
            background: 'transparent',
            color: '#94a3b8',
            fontSize: 13,
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          Cancel
        </button>
        <button
          onClick={() => {
            onConfirm(facility.id, startDate);
            onClose();
          }}
          style={{
            padding: '10px 28px',
            borderRadius: 6,
            border: '1px solid #00ffc840',
            background: 'linear-gradient(135deg,#00ffc825,#00ffc815)',
            color: '#00ffc8',
            fontSize: 13,
            cursor: 'pointer',
            fontWeight: 700,
            letterSpacing: '0.5px',
            boxShadow: '0 0 20px #00ffc815',
          }}
        >
          Confirm — Go Commercial ✦
        </button>
      </div>
    </Modal>
  );
}

function AddTeamMemberModal({ onClose, onSave, existingTeam }) {
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const nextColor = LEAD_COLORS[existingTeam.length % LEAD_COLORS.length];
  const initials = name
    .trim()
    .split(/\s+/)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('')
    .slice(0, 2);
  const id = name.trim().toLowerCase().replace(/\s+/g, '_') + '_' + Date.now();
  const handleSave = () => {
    if (!name.trim() || !role.trim()) return;
    onSave({ id, name: name.trim(), role: role.trim(), initials, color: nextColor });
    onClose();
  };
  return (
    <Modal title="Add Team Member" onClose={onClose} width={420}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <FormField label="Full Name">
          <input
            style={inputStyle}
            placeholder="e.g. Alex Rivera"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </FormField>
        <FormField label="Role / Title">
          <input
            style={inputStyle}
            placeholder="e.g. Regional Sales Director"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          />
        </FormField>
        {name.trim() && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: 16,
              background: '#080d14',
              borderRadius: 8,
              border: '1px solid #1e2d3d',
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: `${nextColor}25`,
                border: `2px solid ${nextColor}60`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                fontWeight: 700,
                color: nextColor,
              }}
            >
              {initials}
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0' }}>{name.trim()}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>{role.trim() || 'Role'}</div>
            </div>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
        <button
          onClick={onClose}
          style={{
            padding: '10px 20px',
            borderRadius: 6,
            border: '1px solid #1e2d3d',
            background: 'transparent',
            color: '#94a3b8',
            fontSize: 13,
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={!name.trim() || !role.trim()}
          style={{
            padding: '10px 28px',
            borderRadius: 6,
            border: '1px solid #06b6d440',
            background:
              name.trim() && role.trim()
                ? 'linear-gradient(135deg,#06b6d420,#06b6d410)'
                : '#1e2d3d',
            color: name.trim() && role.trim() ? '#06b6d4' : '#475569',
            fontSize: 13,
            cursor: name.trim() && role.trim() ? 'pointer' : 'default',
            fontWeight: 700,
          }}
        >
          Add to Team
        </button>
      </div>
    </Modal>
  );
}

// ── DEAL STATUS MODAL (On Hold / Lost) ──
function DealStatusModal({ facility, onClose, onConfirm }) {
  const [newStatus, setNewStatus] = useState(
    facility.dealStatus === 'active' ? 'on_hold' : 'active'
  );
  const [reason, setReason] = useState('');
  return (
    <Modal title="Change Deal Status" onClose={onClose} width={460}>
      <div style={{ fontSize: 16, fontWeight: 600, color: '#e2e8f0', marginBottom: 16 }}>
        {facility.name}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {DEAL_STATUSES.map((s) => (
          <button
            key={s.key}
            onClick={() => setNewStatus(s.key)}
            style={{
              flex: 1,
              padding: '10px 8px',
              borderRadius: 6,
              border: `1px solid ${newStatus === s.key ? s.color + '60' : '#1e2d3d'}`,
              background: newStatus === s.key ? `${s.color}15` : 'transparent',
              color: newStatus === s.key ? s.color : '#475569',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              textAlign: 'center',
            }}
          >
            {s.label}
          </button>
        ))}
      </div>
      {(newStatus === 'on_hold' || newStatus === 'lost') && (
        <FormField label={newStatus === 'lost' ? 'Reason Lost' : 'Hold Reason'}>
          <input
            style={inputStyle}
            placeholder={
              newStatus === 'lost'
                ? 'e.g. Lost to Caresyntax, Budget cut...'
                : 'e.g. Waiting on board approval, Budget cycle...'
            }
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </FormField>
      )}
      <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
        <button
          onClick={onClose}
          style={{
            padding: '10px 20px',
            borderRadius: 6,
            border: '1px solid #1e2d3d',
            background: 'transparent',
            color: '#94a3b8',
            fontSize: 13,
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          Cancel
        </button>
        <button
          onClick={() => {
            onConfirm(facility.id, newStatus, reason);
            onClose();
          }}
          style={{
            padding: '10px 28px',
            borderRadius: 6,
            border: '1px solid #06b6d440',
            background: 'linear-gradient(135deg,#06b6d420,#06b6d410)',
            color: '#06b6d4',
            fontSize: 13,
            cursor: 'pointer',
            fontWeight: 700,
          }}
        >
          Update Status
        </button>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════
// ── SURGEON ADVOCACY PANEL ──
// ═══════════════════════════════

function SurgeonAdvocacyPanel({ facility, onUpdateAdvocates, canEdit = true }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSpecialty, setNewSpecialty] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newCell, setNewCell] = useState('');
  const [newStatus, setNewStatus] = useState('interested');
  const advocates = facility.advocates || [];
  const ratio =
    facility.surgeons > 0 ? Math.round((advocates.length / facility.surgeons) * 100) : 0;
  const ratioColor = ratio >= 10 ? '#00ffc8' : ratio >= 5 ? '#f59e0b' : '#475569';
  const handleAdd = () => {
    if (!newName.trim()) return;
    onUpdateAdvocates(facility.id, [
      ...advocates,
      {
        id: Date.now(),
        name: newName.trim(),
        specialty: newSpecialty.trim() || 'Not specified',
        email: newEmail.trim(),
        cell: newCell.trim(),
        status: newStatus,
        added: todayStr(),
      },
    ]);
    setNewName('');
    setNewSpecialty('');
    setNewEmail('');
    setNewCell('');
    setNewStatus('interested');
    setShowAddForm(false);
  };
  const handleRemove = (advId) =>
    onUpdateAdvocates(
      facility.id,
      advocates.filter((a) => a.id !== advId)
    );
  const handleStatusChange = (advId, ns) =>
    onUpdateAdvocates(
      facility.id,
      advocates.map((a) => (a.id === advId ? { ...a, status: ns } : a))
    );
  const handleContactUpdate = (advId, field, value) =>
    onUpdateAdvocates(
      facility.id,
      advocates.map((a) => (a.id === advId ? { ...a, [field]: value } : a))
    );

  return (
    <div style={{ padding: '20px 28px', borderBottom: '1px solid #1e2d3d' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 14,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              fontSize: 11,
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
              fontWeight: 600,
            }}
          >
            Surgeon Advocacy
          </span>
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: ratioColor,
              fontFamily: "'JetBrains Mono',monospace",
            }}
          >
            {advocates.length}
            <span style={{ color: '#334155', fontWeight: 400 }}>/{facility.surgeons}</span>
          </span>
          <span style={{ fontSize: 10, color: ratioColor }}>({ratio}%)</span>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            style={{
              padding: '4px 12px',
              borderRadius: 4,
              border: '1px solid #06b6d435',
              background: showAddForm ? '#06b6d415' : 'transparent',
              color: '#06b6d4',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {showAddForm ? 'Cancel' : '+ Add'}
          </button>
        )}
      </div>
      <div
        style={{
          width: '100%',
          height: 3,
          borderRadius: 2,
          background: '#1a2332',
          marginBottom: 14,
        }}
      >
        <div
          style={{
            width: `${Math.min(ratio * 2, 100)}%`,
            height: '100%',
            borderRadius: 2,
            background: `linear-gradient(90deg,#06b6d4,${ratioColor})`,
            transition: 'width 0.4s',
          }}
        />
      </div>
      {showAddForm && (
        <div
          style={{
            background: '#080d14',
            border: '1px solid #1e2d3d',
            borderRadius: 8,
            padding: 14,
            marginBottom: 14,
          }}
        >
          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}
          >
            <div>
              <label
                style={{
                  fontSize: 10,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'block',
                  marginBottom: 4,
                }}
              >
                Name
              </label>
              <input
                style={inputStyle}
                placeholder="Dr. Jane Smith"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 10,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'block',
                  marginBottom: 4,
                }}
              >
                Specialty
              </label>
              <input
                style={inputStyle}
                placeholder="Orthopedics"
                value={newSpecialty}
                onChange={(e) => setNewSpecialty(e.target.value)}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 10,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'block',
                  marginBottom: 4,
                }}
              >
                Email
              </label>
              <input
                style={inputStyle}
                placeholder="jsmith@hospital.org"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 10,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'block',
                  marginBottom: 4,
                }}
              >
                Cell
              </label>
              <input
                style={inputStyle}
                placeholder="(555) 123-4567"
                value={newCell}
                onChange={(e) => setNewCell(e.target.value)}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 4, flex: 1 }}>
              {ADVOCATE_STATUSES.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setNewStatus(s.key)}
                  style={{
                    flex: 1,
                    padding: '6px 4px',
                    borderRadius: 4,
                    border: `1px solid ${newStatus === s.key ? s.color + '60' : '#1e2d3d'}`,
                    background: newStatus === s.key ? `${s.color}15` : 'transparent',
                    color: newStatus === s.key ? s.color : '#475569',
                    fontSize: 10,
                    fontWeight: 600,
                    cursor: 'pointer',
                    textAlign: 'center',
                  }}
                >
                  {s.icon} {s.label}
                </button>
              ))}
            </div>
            <button
              onClick={handleAdd}
              disabled={!newName.trim()}
              style={{
                padding: '6px 16px',
                borderRadius: 4,
                border: '1px solid #06b6d440',
                background: newName.trim() ? '#06b6d415' : '#1e2d3d',
                color: newName.trim() ? '#06b6d4' : '#475569',
                fontSize: 11,
                fontWeight: 700,
                cursor: newName.trim() ? 'pointer' : 'default',
              }}
            >
              Add
            </button>
          </div>
        </div>
      )}
      {advocates.length === 0 ? (
        <div style={{ fontSize: 12, color: '#334155', textAlign: 'center', padding: '12px 0' }}>
          No surgeon advocates yet
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {advocates.map((a) => {
            const st = ADVOCATE_STATUS_MAP[a.status] || ADVOCATE_STATUSES[0];
            return (
              <div
                key={a.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 10px',
                  borderRadius: 6,
                  background: '#080d14',
                  border: '1px solid #1e2d3d',
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    color: st.color,
                    flexShrink: 0,
                    width: 14,
                    textAlign: 'center',
                  }}
                >
                  {st.icon}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#e2e8f0',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {a.name}
                  </div>
                  <div style={{ fontSize: 10, color: '#64748b' }}>{a.specialty}</div>
                </div>
                <select
                  value={a.status}
                  onChange={(e) => handleStatusChange(a.id, e.target.value)}
                  disabled={!canEdit}
                  style={{
                    background: 'transparent',
                    border: '1px solid #1e2d3d',
                    borderRadius: 4,
                    color: st.color,
                    fontSize: 10,
                    padding: '2px 4px',
                    cursor: canEdit ? 'pointer' : 'default',
                    outline: 'none',
                    fontWeight: 600,
                  }}
                >
                  {ADVOCATE_STATUSES.map((s) => (
                    <option
                      key={s.key}
                      value={s.key}
                      style={{ background: '#0d1520', color: s.color }}
                    >
                      {s.icon} {s.label}
                    </option>
                  ))}
                </select>
                {canEdit && (
                  <button
                    onClick={() => handleRemove(a.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#334155',
                      cursor: 'pointer',
                      fontSize: 12,
                      padding: '2px 4px',
                      transition: 'color 0.15s',
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.color = '#ef4444')}
                    onMouseOut={(e) => (e.currentTarget.style.color = '#334155')}
                  >
                    ✕
                  </button>
                )}
                <div
                  style={{ display: 'flex', gap: 6, marginTop: 4, marginLeft: 22, width: '100%' }}
                >
                  <input
                    value={a.email || ''}
                    onChange={(e) => handleContactUpdate(a.id, 'email', e.target.value)}
                    disabled={!canEdit}
                    placeholder="email"
                    style={{
                      ...inputStyle,
                      padding: '4px 8px',
                      fontSize: 10,
                      flex: 1,
                      background: 'transparent',
                      border: '1px solid #1e2d3d',
                    }}
                  />
                  <input
                    value={a.cell || ''}
                    onChange={(e) => handleContactUpdate(a.id, 'cell', e.target.value)}
                    disabled={!canEdit}
                    placeholder="cell"
                    style={{
                      ...inputStyle,
                      padding: '4px 8px',
                      fontSize: 10,
                      flex: 1,
                      background: 'transparent',
                      border: '1px solid #1e2d3d',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════
// ── ACTIVITY LOG PANEL ──
// ═══════════════════════════════

function ActivityLogPanel({ facility, onAddActivity, team, canEdit = true, currentUserId = '' }) {
  const [showAdd, setShowAdd] = useState(false);
  const [text, setText] = useState('');
  const [who, setWho] = useState(canEdit ? team[0]?.id || '' : currentUserId);
  const log = facility.activityLog || [];
  const handleAdd = () => {
    if (!text.trim()) return;
    onAddActivity(facility.id, { date: todayStr(), who, text: text.trim() });
    setText('');
    setShowAdd(false);
  };
  return (
    <div style={{ padding: '20px 28px', borderBottom: '1px solid #1e2d3d' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <span
          style={{
            fontSize: 11,
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '1.5px',
            fontWeight: 600,
          }}
        >
          Activity Log
        </span>
        {
          <button
            onClick={() => setShowAdd(!showAdd)}
            style={{
              padding: '4px 12px',
              borderRadius: 4,
              border: '1px solid #06b6d435',
              background: showAdd ? '#06b6d415' : 'transparent',
              color: '#06b6d4',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {showAdd ? 'Cancel' : '+ Log'}
          </button>
        }
      </div>
      {showAdd && (
        <div
          style={{
            background: '#080d14',
            border: '1px solid #1e2d3d',
            borderRadius: 8,
            padding: 12,
            marginBottom: 12,
          }}
        >
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            {canEdit ? (
              <select
                value={who}
                onChange={(e) => setWho(e.target.value)}
                style={{
                  ...inputStyle,
                  padding: '6px 10px',
                  fontSize: 11,
                  width: 'auto',
                  minWidth: 100,
                }}
              >
                {team.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.initials}
                  </option>
                ))}
              </select>
            ) : (
              <TeamBadge memberId={currentUserId} team={team} />
            )}
            <input
              style={{ ...inputStyle, padding: '6px 10px', fontSize: 12, flex: 1 }}
              placeholder="What happened?"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAdd();
              }}
            />
            <button
              onClick={handleAdd}
              disabled={!text.trim()}
              style={{
                padding: '6px 14px',
                borderRadius: 4,
                border: '1px solid #06b6d440',
                background: text.trim() ? '#06b6d415' : '#1e2d3d',
                color: text.trim() ? '#06b6d4' : '#475569',
                fontSize: 11,
                fontWeight: 700,
                cursor: text.trim() ? 'pointer' : 'default',
                flexShrink: 0,
              }}
            >
              Add
            </button>
          </div>
        </div>
      )}
      {log.length === 0 ? (
        <div style={{ fontSize: 12, color: '#334155', textAlign: 'center', padding: '8px 0' }}>
          No activity logged yet
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            maxHeight: 200,
            overflowY: 'auto',
          }}
        >
          {[...log].reverse().map((entry, i) => {
            const member = team.find((t) => t.id === entry.who);
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 8,
                  padding: '6px 0',
                  borderBottom: i < log.length - 1 ? '1px solid #1e2d3d20' : 'none',
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    color: '#334155',
                    fontFamily: 'monospace',
                    flexShrink: 0,
                    width: 60,
                    paddingTop: 2,
                  }}
                >
                  {entry.date
                    ? new Date(entry.date + 'T00:00:00').toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })
                    : ''}
                </div>
                {member && (
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: `${member.color}25`,
                      border: `1px solid ${member.color}50`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 8,
                      fontWeight: 700,
                      color: member.color,
                      flexShrink: 0,
                    }}
                  >
                    {member.initials}
                  </div>
                )}
                <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.4, flex: 1 }}>
                  {entry.text}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════
// ── REVENUE FORECAST CHART ──
// ═══════════════════════════════

function RevenueForecastChart({ facilities }) {
  const forecastData = useMemo(() => {
    const months = [];
    const start = new Date('2025-03-01');
    for (let i = 0; i < 18; i++) {
      const d = new Date(start);
      d.setMonth(d.getMonth() + i);
      const label = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      const dateStr = d.toISOString().split('T')[0];
      let contracted = 0;
      let projected = 0;
      facilities
        .filter((f) => f.dealStatus !== 'lost')
        .forEach((f) => {
          const proj = f.projectedStart || '2027-12-31';
          if (['signed', 'onboarding', 'commercial'].includes(f.stage)) contracted += f.dealValue;
          else if (proj <= dateStr) projected += f.dealValue * (STAGE_MAP[f.stage]?.prob || 0);
        });
      months.push({
        name: label,
        contracted: Math.round(contracted / 1000),
        projected: Math.round((contracted + projected) / 1000),
      });
    }
    return months;
  }, [facilities]);

  return (
    <div
      style={{
        background: 'linear-gradient(135deg,#0d1520,#111d2e)',
        border: '1px solid #1e2d3d',
        borderRadius: 8,
        padding: '20px 24px',
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: '#64748b',
          textTransform: 'uppercase',
          letterSpacing: '1.5px',
          marginBottom: 16,
          fontWeight: 600,
        }}
      >
        Revenue Forecast (18 months)
      </div>
      <ResponsiveContainer width="100%" height={140}>
        <AreaChart data={forecastData}>
          <defs>
            <linearGradient id="gContracted" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gProjected" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.2} />
              <stop offset="100%" stopColor="#06b6d4" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="name"
            tick={{ fill: '#475569', fontSize: 9 }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: '#475569', fontSize: 9 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `$${v}K`}
            width={50}
          />
          <Tooltip
            contentStyle={{
              background: '#0d1520',
              border: '1px solid #1e2d3d',
              borderRadius: 6,
              fontSize: 11,
            }}
            formatter={(v) => [`$${v}K`, '']}
            labelStyle={{ color: '#64748b' }}
          />
          <Area
            type="monotone"
            dataKey="projected"
            stroke="#06b6d4"
            strokeWidth={1.5}
            fill="url(#gProjected)"
            name="Projected"
          />
          <Area
            type="monotone"
            dataKey="contracted"
            stroke="#10b981"
            strokeWidth={2}
            fill="url(#gContracted)"
            name="Contracted"
          />
        </AreaChart>
      </ResponsiveContainer>
      <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 12, height: 3, borderRadius: 2, background: '#10b981' }} />
          <span style={{ fontSize: 10, color: '#64748b' }}>Contracted</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 12, height: 3, borderRadius: 2, background: '#06b6d4' }} />
          <span style={{ fontSize: 10, color: '#64748b' }}>Projected (weighted)</span>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════
// ── NETWORK PENETRATION ──
// ═══════════════════════════════

function NetworkPenetrationPanel({
  facilities,
  networks: networkList = [],
  canEdit = false,
  onSelectNetwork,
}) {
  const networks = useMemo(() => {
    // Build a lookup from network id → registered network
    const tamById = {};
    for (const n of networkList) {
      tamById[n.id] = n;
    }
    // Build map from facilities
    const map = {};
    facilities.forEach((f) => {
      const netId = f.networkId || '_none';
      const netInfo = f.networkId ? tamById[f.networkId] : null;
      const name = netInfo?.name || f.network || 'Unassigned';
      if (!map[netId])
        map[netId] = {
          id: f.networkId || null,
          name,
          count: 0,
          value: 0,
          tam: netInfo?.totalFacilities ?? null,
        };
      map[netId].count++;
      map[netId].value += f.dealValue;
    });
    // Include registered networks that have no facilities yet
    for (const n of networkList) {
      if (!map[n.id]) {
        map[n.id] = { id: n.id, name: n.name, count: 0, value: 0, tam: n.totalFacilities ?? null };
      }
    }
    return Object.values(map).sort((a, b) => b.value - a.value);
  }, [facilities, networkList]);

  return (
    <div
      style={{
        background: 'linear-gradient(135deg,#0d1520,#111d2e)',
        border: '1px solid #1e2d3d',
        borderRadius: 8,
        padding: '20px 24px',
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: '#64748b',
          textTransform: 'uppercase',
          letterSpacing: '1.5px',
          marginBottom: 12,
          fontWeight: 600,
        }}
      >
        Network Penetration
        {canEdit && onSelectNetwork && (
          <span
            style={{
              fontSize: 9,
              color: '#334155',
              fontWeight: 400,
              letterSpacing: '0.5px',
              marginLeft: 8,
              textTransform: 'none',
            }}
          >
            Click to edit
          </span>
        )}
      </div>
      {(() => {
        const maxValue = Math.max(...networks.map((n) => n.value), 1);
        return networks.map((n) => {
          const pct = n.tam ? Math.round((n.count / n.tam) * 100) : null;
          const barPct = n.tam
            ? Math.min((n.count / n.tam) * 100, 100)
            : n.value > 0
              ? (n.value / maxValue) * 100
              : 0;
          const barColor =
            pct !== null ? (pct >= 10 ? '#00ffc8' : pct >= 5 ? '#f59e0b' : '#06b6d4') : '#06b6d4';
          const isClickable = canEdit && onSelectNetwork;
          return (
            <div
              key={n.name}
              onClick={isClickable ? () => onSelectNetwork(n) : undefined}
              style={{
                marginBottom: 4,
                padding: '6px 8px',
                borderRadius: 6,
                cursor: isClickable ? 'pointer' : 'default',
                transition: 'background 0.15s',
              }}
              onMouseOver={
                isClickable
                  ? (e) => {
                      e.currentTarget.style.background = '#ffffff08';
                    }
                  : undefined
              }
              onMouseOut={
                isClickable
                  ? (e) => {
                      e.currentTarget.style.background = 'transparent';
                    }
                  : undefined
              }
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 3,
                }}
              >
                <span style={{ fontSize: 12, color: '#94a3b8' }}>{n.name}</span>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  {n.tam != null && (
                    <span style={{ fontSize: 10, color: '#475569' }}>
                      {n.count}/{n.tam}
                    </span>
                  )}
                  {pct !== null && (
                    <span
                      style={{
                        fontSize: 10,
                        color: pct >= 10 ? '#00ffc8' : pct >= 5 ? '#f59e0b' : '#475569',
                        fontWeight: 600,
                      }}
                    >
                      {pct}%
                    </span>
                  )}
                  <span
                    style={{
                      fontSize: 12,
                      color: '#e2e8f0',
                      fontWeight: 600,
                      fontFamily: "'JetBrains Mono',monospace",
                      width: 50,
                      textAlign: 'right',
                    }}
                  >
                    {formatCurrency(n.value)}
                  </span>
                </div>
              </div>
              <div style={{ width: '100%', height: 3, borderRadius: 2, background: '#1a2332' }}>
                <div
                  style={{
                    width: `${barPct}%`,
                    height: '100%',
                    borderRadius: 2,
                    background: barColor,
                    transition: 'width 0.4s',
                  }}
                />
              </div>
            </div>
          );
        });
      })()}
    </div>
  );
}

// ═══════════════════════════════
// ── ADD NETWORK MODAL ──
// ═══════════════════════════════

function AddNetworkModal({ onClose, onSave }) {
  const [form, setForm] = useState({ name: '', totalFacilities: '' });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const handleSave = () => {
    if (!form.name.trim()) return;
    const tam = form.totalFacilities.trim() === '' ? null : parseInt(form.totalFacilities, 10);
    if (form.totalFacilities.trim() !== '' && (isNaN(tam) || tam < 0)) return;
    onSave(form.name.trim(), tam);
    onClose();
  };
  return (
    <Modal title="Add New Network" onClose={onClose} width={420}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
        <FormField label="Network Name" span={1}>
          <input
            autoFocus
            style={inputStyle}
            placeholder="e.g. HCA Healthcare"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
          />
        </FormField>
        <FormField label="Total Facilities in Network" span={1}>
          <input
            style={inputStyle}
            type="number"
            min="0"
            placeholder="e.g. 186"
            value={form.totalFacilities}
            onChange={(e) => set('totalFacilities', e.target.value)}
          />
        </FormField>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
        <button
          onClick={onClose}
          style={{
            padding: '8px 20px',
            borderRadius: 6,
            border: '1px solid #1e2d3d',
            background: 'transparent',
            color: '#64748b',
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          style={{
            padding: '8px 20px',
            borderRadius: 6,
            border: 'none',
            background: !form.name.trim() ? '#1e2d3d' : 'linear-gradient(135deg,#06b6d4,#00ffc8)',
            color: !form.name.trim() ? '#475569' : '#080d14',
            fontSize: 12,
            fontWeight: 700,
            cursor: form.name.trim() ? 'pointer' : 'default',
          }}
        >
          Add Network
        </button>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════
// ── NETWORK DETAIL PANEL ──
// ═══════════════════════════════

function NetworkDetailPanel({ network, facilities, onClose, onUpdate, onDelete }) {
  const [editName, setEditName] = useState(network.name);
  const [editTam, setEditTam] = useState(network.tam != null ? String(network.tam) : '');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const networkFacilities = useMemo(
    () => facilities.filter((f) => f.networkId === network.id),
    [facilities, network.id]
  );

  const handleSave = () => {
    const tam = editTam.trim() === '' ? null : parseInt(editTam, 10);
    if (editTam.trim() !== '' && (isNaN(tam) || tam < 0)) return;
    onUpdate(network.id, editName.trim(), tam);
  };

  const hasChanges =
    editName.trim() !== network.name ||
    (editTam.trim() === '' ? null : parseInt(editTam, 10)) !== network.tam;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: 440,
        background: 'linear-gradient(180deg,#0a1018,#0d1520)',
        borderLeft: '1px solid #1e2d3d',
        zIndex: 1000,
        overflowY: 'auto',
        boxShadow: '-20px 0 60px rgba(0,0,0,0.5)',
      }}
    >
      {/* Header */}
      <div style={{ padding: '24px 28px', borderBottom: '1px solid #1e2d3d' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div
              style={{
                fontSize: 10,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '1.5px',
                marginBottom: 4,
              }}
            >
              Network
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#e2e8f0' }}>{network.name}</div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: '1px solid #1e2d3d',
              borderRadius: 4,
              color: '#64748b',
              cursor: 'pointer',
              padding: '4px 10px',
              fontSize: 12,
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>{network.count}</div>
            <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>In Pipeline</div>
          </div>
          {network.tam != null && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>{network.tam}</div>
              <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>Total Facilities</div>
            </div>
          )}
          {network.tam != null && network.tam > 0 && (
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  color:
                    Math.round((network.count / network.tam) * 100) >= 10
                      ? '#00ffc8'
                      : Math.round((network.count / network.tam) * 100) >= 5
                        ? '#f59e0b'
                        : '#06b6d4',
                }}
              >
                {Math.round((network.count / network.tam) * 100)}%
              </div>
              <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>Penetration</div>
            </div>
          )}
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>
              {formatCurrency(network.value)}
            </div>
            <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>Pipeline Value</div>
          </div>
        </div>
      </div>

      {/* Edit Fields */}
      <div style={{ padding: '20px 28px', borderBottom: '1px solid #1e2d3d' }}>
        <div
          style={{
            fontSize: 10,
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '1.5px',
            marginBottom: 12,
            fontWeight: 600,
          }}
        >
          Edit Network
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: '#475569', marginBottom: 4, fontWeight: 600 }}>
            Network Name
          </div>
          <input
            style={inputStyle}
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: '#475569', marginBottom: 4, fontWeight: 600 }}>
            Total Facilities in Network
          </div>
          <input
            style={inputStyle}
            type="number"
            min="0"
            placeholder="e.g. 186"
            value={editTam}
            onChange={(e) => setEditTam(e.target.value)}
          />
        </div>
        {hasChanges && (
          <button
            onClick={handleSave}
            style={{
              padding: '8px 20px',
              borderRadius: 6,
              border: 'none',
              background: 'linear-gradient(135deg,#06b6d4,#00ffc8)',
              color: '#080d14',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              width: '100%',
            }}
          >
            Save Changes
          </button>
        )}
      </div>

      {/* Facilities in this network */}
      <div style={{ padding: '20px 28px', borderBottom: '1px solid #1e2d3d' }}>
        <div
          style={{
            fontSize: 10,
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '1.5px',
            marginBottom: 12,
            fontWeight: 600,
          }}
        >
          Facilities ({networkFacilities.length})
        </div>
        {networkFacilities.length === 0 ? (
          <div style={{ fontSize: 12, color: '#334155', fontStyle: 'italic' }}>
            No facilities in this network yet
          </div>
        ) : (
          networkFacilities.map((f) => (
            <div
              key={f.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 0',
                borderBottom: '1px solid #1e2d3d20',
              }}
            >
              <div>
                <div style={{ fontSize: 12, color: '#e2e8f0', fontWeight: 500 }}>{f.name}</div>
                <div style={{ fontSize: 10, color: '#475569' }}>
                  {f.city}, {f.state}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <StageBadge stage={f.stage} />
                <div
                  style={{
                    fontSize: 11,
                    color: '#e2e8f0',
                    fontWeight: 600,
                    fontFamily: "'JetBrains Mono',monospace",
                    marginTop: 4,
                  }}
                >
                  {formatCurrency(f.dealValue)}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Delete */}
      <div style={{ padding: '20px 28px' }}>
        {!confirmDelete ? (
          <button
            onClick={() => setConfirmDelete(true)}
            style={{
              padding: '8px 20px',
              borderRadius: 6,
              border: '1px solid #1e2d3d',
              background: 'transparent',
              color: '#475569',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              width: '100%',
              transition: 'all 0.15s',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = '#ef444460';
              e.currentTarget.style.color = '#ef4444';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = '#1e2d3d';
              e.currentTarget.style.color = '#475569';
            }}
          >
            Delete Network
          </button>
        ) : (
          <div>
            <div style={{ fontSize: 12, color: '#ef4444', marginBottom: 8, fontWeight: 600 }}>
              {networkFacilities.length > 0
                ? `Cannot delete — ${networkFacilities.length} facility${networkFacilities.length === 1 ? '' : 'ies'} assigned. Reassign or remove them first.`
                : 'Are you sure you want to delete this network?'}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {networkFacilities.length === 0 && (
                <button
                  onClick={() => onDelete(network.id, network.name)}
                  style={{
                    flex: 1,
                    padding: '8px',
                    borderRadius: 6,
                    border: '1px solid #ef4444',
                    background: '#ef444420',
                    color: '#ef4444',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Confirm Delete
                </button>
              )}
              <button
                onClick={() => setConfirmDelete(false)}
                style={{
                  flex: 1,
                  padding: '8px',
                  borderRadius: 6,
                  border: '1px solid #1e2d3d',
                  background: 'transparent',
                  color: '#64748b',
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════
// ── DETAIL PANEL ──
// ═══════════════════════════════

function FacilityDetailPanel({
  facility,
  onClose,
  onFlipToCommercial,
  onStageChange,
  onUpdateAdvocates,
  onUpdateField,
  onAddActivity,
  onSetDealStatus,
  onDelete,
  onEdit,
  team,
  canEdit = true,
  currentUserId = '',
}) {
  const f = facility;
  const stageObj = STAGE_MAP[f.stage];
  const progress = ((stageObj.idx + 1) / STAGES.length) * 100;
  const isOverdue = f.nextActionDate && new Date(f.nextActionDate) < new Date(todayStr());
  const [showMoveDropdown, setShowMoveDropdown] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const dynHealth = calcDynamicHealth(f);
  const velocity = getVelocityStatus(f);
  const prevStage = stageObj.idx > 0 ? STAGES[stageObj.idx - 1] : null;
  const nextStage = stageObj.idx < STAGES.length - 2 ? STAGES[stageObj.idx + 1] : null;
  const isCommercial = f.stage === 'commercial';
  const isInactive = f.dealStatus === 'lost' || f.dealStatus === 'on_hold';

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: 540,
        background: 'linear-gradient(180deg,#0a1018,#0d1520)',
        borderLeft: '1px solid #1e2d3d',
        zIndex: 1000,
        overflowY: 'auto',
        boxShadow: '-20px 0 60px rgba(0,0,0,0.5)',
      }}
    >
      {/* Header */}
      <div style={{ padding: '24px 28px', borderBottom: '1px solid #1e2d3d' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div
              style={{
                fontSize: 10,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '1.5px',
                marginBottom: 4,
              }}
            >
              {getCountryFlag(f.country)} {f.city}, {f.state}
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#e2e8f0' }}>{f.name}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{f.network}</div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: '1px solid #1e2d3d',
              borderRadius: 4,
              color: '#64748b',
              cursor: 'pointer',
              padding: '4px 10px',
              fontSize: 12,
            }}
          >
            ✕
          </button>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 10,
            marginTop: 16,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <StageBadge stage={f.stage} />
          <DealStatusBadge status={f.dealStatus} />
          <HealthIndicator value={dynHealth} />
          <VelocityBadge facility={f} />
          <span style={{ fontSize: 22, fontWeight: 700, color: '#e2e8f0', marginLeft: 'auto' }}>
            {formatCurrency(f.dealValue)}
          </span>
        </div>
        <div style={{ marginTop: 12 }}>
          <ProgressBar stage={f.stage} />
          <div style={{ fontSize: 10, color: '#475569', marginTop: 4, textAlign: 'right' }}>
            {Math.round(progress)}% · win prob: {Math.round(stageObj.prob * 100)}%
          </div>
        </div>
        {/* Edit, Deal status & delete */}
        {canEdit && (
          <div style={{ display: 'flex', alignItems: 'center', marginTop: 10, gap: 8 }}>
            <button
              onClick={() => onEdit(f)}
              style={{
                padding: '4px 10px',
                borderRadius: 4,
                border: '1px solid #00ffc840',
                background: 'transparent',
                color: '#00ffc8',
                fontSize: 10,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              Edit Facility
            </button>
            <button
              onClick={() => onSetDealStatus(f)}
              style={{
                padding: '4px 10px',
                borderRadius: 4,
                border: '1px solid #1e2d3d',
                background: 'transparent',
                color: '#64748b',
                fontSize: 10,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              {isInactive ? 'Reactivate / Change Status' : 'Mark On Hold / Lost'}
            </button>
            {!confirmDelete ? (
              <button
                onClick={() => setConfirmDelete(true)}
                style={{
                  marginLeft: 'auto',
                  padding: '4px 10px',
                  borderRadius: 4,
                  border: '1px solid #ef444435',
                  background: 'transparent',
                  color: '#ef4444',
                  fontSize: 10,
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                Delete
              </button>
            ) : (
              <div
                style={{
                  marginLeft: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span style={{ fontSize: 10, color: '#ef4444', fontWeight: 600 }}>
                  Delete permanently?
                </span>
                <button
                  onClick={() => {
                    onDelete(f.id);
                    onClose();
                  }}
                  style={{
                    padding: '4px 10px',
                    borderRadius: 4,
                    border: '1px solid #ef444460',
                    background: '#ef444415',
                    color: '#ef4444',
                    fontSize: 10,
                    cursor: 'pointer',
                    fontWeight: 700,
                  }}
                >
                  Yes
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  style={{
                    padding: '4px 10px',
                    borderRadius: 4,
                    border: '1px solid #1e2d3d',
                    background: 'transparent',
                    color: '#64748b',
                    fontSize: 10,
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  No
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Stage Controls */}
      {!isCommercial && !isInactive && (
        <div
          style={{
            padding: '16px 28px',
            borderBottom: '1px solid #1e2d3d',
            background: 'linear-gradient(135deg,#0d152080,#111d2e40)',
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
              marginBottom: 12,
              fontWeight: 600,
            }}
          >
            Move Stage
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {prevStage ? (
              <button
                onClick={() => onStageChange(f.id, prevStage.key)}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  borderRadius: 6,
                  border: `1px solid ${prevStage.color}35`,
                  background: `${prevStage.color}08`,
                  color: prevStage.color,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  transition: 'all 0.2s',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = `${prevStage.color}18`;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = `${prevStage.color}08`;
                }}
              >
                <span style={{ fontSize: 14 }}>←</span> {prevStage.label}
              </button>
            ) : (
              <div style={{ flex: 1 }} />
            )}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowMoveDropdown(!showMoveDropdown)}
                style={{
                  padding: '10px 14px',
                  borderRadius: 6,
                  border: '1px solid #1e2d3d',
                  background: '#080d14',
                  color: '#94a3b8',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  whiteSpace: 'nowrap',
                }}
              >
                Move to... <span style={{ fontSize: 8, opacity: 0.6 }}>▼</span>
              </button>
              {showMoveDropdown && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    marginTop: 4,
                    background: '#0d1520',
                    border: '1px solid #1e2d3d',
                    borderRadius: 8,
                    padding: 4,
                    zIndex: 10,
                    minWidth: 180,
                    boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
                  }}
                >
                  {STAGES.filter((s) => s.key !== 'commercial' && s.key !== f.stage).map((s) => (
                    <button
                      key={s.key}
                      onClick={() => {
                        onStageChange(f.id, s.key);
                        setShowMoveDropdown(false);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: 4,
                        border: 'none',
                        background: 'transparent',
                        color: '#cbd5e1',
                        fontSize: 12,
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'background 0.15s',
                      }}
                      onMouseOver={(e) => (e.currentTarget.style.background = `${s.color}15`)}
                      onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: s.color,
                          flexShrink: 0,
                        }}
                      />
                      <span>{s.label}</span>
                      {s.idx < stageObj.idx && (
                        <span style={{ marginLeft: 'auto', fontSize: 10, color: '#475569' }}>
                          back
                        </span>
                      )}
                      {s.idx > stageObj.idx && (
                        <span style={{ marginLeft: 'auto', fontSize: 10, color: s.color }}>→</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {nextStage ? (
              <button
                onClick={() => onStageChange(f.id, nextStage.key)}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  borderRadius: 6,
                  border: `1px solid ${nextStage.color}35`,
                  background: `${nextStage.color}08`,
                  color: nextStage.color,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  transition: 'all 0.2s',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = `${nextStage.color}18`;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = `${nextStage.color}08`;
                }}
              >
                {nextStage.label} <span style={{ fontSize: 14 }}>→</span>
              </button>
            ) : (
              <div style={{ flex: 1 }} />
            )}
          </div>
        </div>
      )}

      {/* Facility Info */}
      <div style={{ padding: '20px 28px', borderBottom: '1px solid #1e2d3d' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <div>
            <div
              style={{
                fontSize: 10,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}
            >
              ORs
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#e2e8f0' }}>{f.ors}</div>
          </div>
          <div>
            <div
              style={{
                fontSize: 10,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}
            >
              Surgeons
            </div>
            <EditableNumber
              value={f.surgeons}
              onSave={(v) => onUpdateField(f.id, 'surgeons', v)}
              canEdit={canEdit}
            />
          </div>
          <div>
            <div
              style={{
                fontSize: 10,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}
            >
              Lead
            </div>
            <LeadPicker
              currentLeadId={f.lead}
              team={team}
              canEdit={canEdit}
              onSelect={(id) => onUpdateField(f.id, 'leadId', id || null)}
            />
          </div>
        </div>
      </div>

      {/* Key Contacts */}
      <div style={{ padding: '20px 28px', borderBottom: '1px solid #1e2d3d' }}>
        <div
          style={{
            fontSize: 11,
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '1.5px',
            marginBottom: 12,
            fontWeight: 600,
          }}
        >
          Key Contacts
        </div>
        {[
          { role: 'Champion', field: 'champion' },
          { role: 'Economic Buyer', field: 'economicBuyer' },
          { role: 'Technical Buyer', field: 'technicalBuyer' },
        ].map((c, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 11, color: '#64748b', width: 110, flexShrink: 0 }}>
              {c.role}
            </span>
            <div style={{ flex: 1 }}>
              <EditableField
                value={f[c.field]}
                onSave={(v) => onUpdateField(f.id, c.field, v)}
                placeholder={`Add ${c.role.toLowerCase()}...`}
                disabled={!canEdit}
              />
            </div>
          </div>
        ))}
      </div>

      <SurgeonAdvocacyPanel facility={f} onUpdateAdvocates={onUpdateAdvocates} canEdit={canEdit} />

      {/* Next Action */}
      <div
        style={{
          padding: '16px 28px',
          borderBottom: '1px solid #1e2d3d',
          background: isOverdue ? '#ef444410' : 'transparent',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
          }}
        >
          <span
            style={{
              fontSize: 11,
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
              fontWeight: 600,
            }}
          >
            Next Action
          </span>
          {isOverdue && (
            <span
              style={{
                fontSize: 10,
                color: '#ef4444',
                fontWeight: 700,
                textTransform: 'uppercase',
              }}
            >
              ⚠ Overdue
            </span>
          )}
        </div>
        <EditableField
          value={f.nextAction}
          onSave={(v) => onUpdateField(f.id, 'nextAction', v)}
          placeholder="What needs to happen next?"
          disabled={!canEdit}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <span style={{ fontSize: 11, color: isOverdue ? '#ef4444' : '#64748b' }}>Due:</span>
          <DatePicker
            value={f.nextActionDate || ''}
            onChange={(v) => onUpdateField(f.id, 'nextActionDate', v)}
            compact
            overdue={isOverdue}
            disabled={!canEdit}
            placeholder="Set due date..."
          />
        </div>
      </div>

      {/* Activity Log */}
      <ActivityLogPanel
        facility={f}
        onAddActivity={onAddActivity}
        team={team}
        canEdit={canEdit}
        currentUserId={currentUserId}
      />

      {/* Milestones */}
      <div style={{ padding: '20px 28px', borderBottom: '1px solid #1e2d3d' }}>
        <div
          style={{
            fontSize: 11,
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '1.5px',
            marginBottom: 16,
            fontWeight: 600,
          }}
        >
          Milestone Timeline
        </div>
        {STAGES.map((s) => {
          const date = f.milestones[s.key];
          const isCurrent = s.key === f.stage;
          return (
            <div
              key={s.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 6,
                opacity: date ? 1 : 0.3,
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: date ? s.color : '#1e2d3d',
                  border: isCurrent ? `2px solid ${s.color}` : 'none',
                  boxShadow: isCurrent ? `0 0 8px ${s.color}60` : 'none',
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: 11, color: date ? '#94a3b8' : '#334155', width: 90 }}>
                {s.label}
              </span>
              <span
                style={{
                  fontSize: 11,
                  color: date ? '#64748b' : '#1e2d3d',
                  fontFamily: 'monospace',
                }}
              >
                {date ? formatDate(date) : '—'}
              </span>
            </div>
          );
        })}
      </div>

      {/* Editable fields */}
      <div style={{ padding: '20px 28px', borderBottom: '1px solid #1e2d3d' }}>
        <div
          style={{
            fontSize: 11,
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '1.5px',
            marginBottom: 8,
            fontWeight: 600,
          }}
        >
          Competitive Landscape
        </div>
        <EditableField
          value={f.competitor}
          onSave={(v) => onUpdateField(f.id, 'competitor', v)}
          placeholder="Who else is in the building?"
          disabled={!canEdit}
        />
      </div>
      <div style={{ padding: '20px 28px', borderBottom: '1px solid #1e2d3d' }}>
        <div
          style={{
            fontSize: 11,
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '1.5px',
            marginBottom: 8,
            fontWeight: 600,
          }}
        >
          Compliance & Procurement
        </div>
        <EditableTextarea
          value={f.compliance}
          onSave={(v) => onUpdateField(f.id, 'compliance', v)}
          placeholder="BAA, vendor credentialing, GDPR..."
          disabled={!canEdit}
        />
      </div>
      <div style={{ padding: '20px 28px', borderBottom: '1px solid #1e2d3d' }}>
        <div
          style={{
            fontSize: 11,
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '1.5px',
            marginBottom: 8,
            fontWeight: 600,
          }}
        >
          Notes
        </div>
        <EditableTextarea
          value={f.notes}
          onSave={(v) => onUpdateField(f.id, 'notes', v)}
          placeholder="Strategic context, risks..."
        />
      </div>

      {/* Flip to Commercial */}
      {canEdit && !isCommercial && !isInactive && (
        <div style={{ padding: '20px 28px' }}>
          <button
            onClick={() => onFlipToCommercial(f)}
            style={{
              width: '100%',
              padding: '14px',
              borderRadius: 6,
              border: '1px solid #00ffc840',
              background: 'linear-gradient(135deg,#00ffc815,#00ffc808)',
              color: '#00ffc8',
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              transition: 'all 0.2s',
            }}
            onMouseOver={(e) => {
              e.target.style.background = 'linear-gradient(135deg,#00ffc830,#00ffc818)';
              e.target.style.boxShadow = '0 0 24px #00ffc820';
            }}
            onMouseOut={(e) => {
              e.target.style.background = 'linear-gradient(135deg,#00ffc815,#00ffc808)';
              e.target.style.boxShadow = 'none';
            }}
          >
            ✦ Flip to Commercial
          </button>
          <div style={{ fontSize: 11, color: '#475569', marginTop: 8, textAlign: 'center' }}>
            Projected: {formatDate(f.projectedStart)}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════
// ── PIPELINE FUNNEL ──
// ═══════════════════════════════

function PipelineFunnel({ facilities }) {
  const stageCounts = STAGES.map((s) => ({
    ...s,
    count: facilities.filter((f) => f.stage === s.key).length,
  }));
  const maxCount = Math.max(...stageCounts.map((s) => s.count), 1);
  return (
    <div
      style={{
        background: 'linear-gradient(135deg,#0d1520,#111d2e)',
        border: '1px solid #1e2d3d',
        borderRadius: 8,
        padding: '20px 24px',
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: '#64748b',
          textTransform: 'uppercase',
          letterSpacing: '1.5px',
          marginBottom: 16,
          fontWeight: 600,
        }}
      >
        Pipeline Funnel
      </div>
      <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 80 }}>
        {stageCounts.map((s) => (
          <div
            key={s.key}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700 }}>{s.count || ''}</span>
            <div
              style={{
                width: '100%',
                height: Math.max((s.count / maxCount) * 56, s.count > 0 ? 6 : 0),
                borderRadius: 3,
                background: `linear-gradient(180deg,${s.color},${s.color}80)`,
                transition: 'height 0.4s',
                boxShadow: s.count > 0 ? `0 0 8px ${s.color}30` : 'none',
              }}
            />
            <span
              style={{
                fontSize: 8,
                color: '#475569',
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                textAlign: 'center',
              }}
            >
              {s.label.replace('Legal/Contract', 'Legal')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════
// ── EXPORT FUNCTION ──
// ═══════════════════════════════

function generateExport(facilities, team) {
  const contracted = ['signed', 'onboarding', 'commercial'];
  const active = facilities.filter((f) => f.dealStatus !== 'lost');
  const totalPipeline = active.reduce((s, f) => s + f.dealValue, 0);
  const contractedARR = active
    .filter((f) => contracted.includes(f.stage))
    .reduce((s, f) => s + f.dealValue, 0);
  const weightedPipeline = active.reduce(
    (s, f) => s + f.dealValue * (STAGE_MAP[f.stage]?.prob || 0),
    0
  );

  let text = `eXeX COMMAND CENTER — PIPELINE SUMMARY\n${'═'.repeat(50)}\nGenerated: ${new Date().toLocaleDateString()}\n\n`;
  text += `HEADLINE METRICS\n${'─'.repeat(30)}\nTotal Pipeline:     ${formatCurrency(totalPipeline)} (${active.length} facilities)\nContracted ARR:     ${formatCurrency(contractedARR)}\nWeighted Pipeline:  ${formatCurrency(weightedPipeline)}\n\n`;
  text += `PIPELINE BY STAGE\n${'─'.repeat(30)}\n`;
  STAGES.forEach((s) => {
    const facs = active.filter((f) => f.stage === s.key);
    if (facs.length) {
      text += `\n${s.label.toUpperCase()} (${facs.length})\n`;
      facs.forEach((f) => {
        text += `  • ${f.name} — ${formatCurrency(f.dealValue)} — ${f.city}, ${f.state} ${getCountryFlag(f.country)}\n`;
      });
    }
  });
  const lost = facilities.filter((f) => f.dealStatus === 'lost');
  const held = facilities.filter((f) => f.dealStatus === 'on_hold');
  if (held.length) {
    text += `\nON HOLD (${held.length})\n`;
    held.forEach((f) => {
      text += `  • ${f.name} — ${formatCurrency(f.dealValue)}\n`;
    });
  }
  if (lost.length) {
    text += `\nLOST (${lost.length})\n`;
    lost.forEach((f) => {
      text += `  • ${f.name} — ${formatCurrency(f.dealValue)}\n`;
    });
  }
  return text;
}

// ═══════════════════════════════
// ── MAIN DASHBOARD ──
// ═══════════════════════════════

export function CommandCenterPage() {
  const navigate = useNavigate();
  const { hasRole, user } = useAuth();
  const canEdit = hasRole('mc-admin') || hasRole('cc-editor');
  const currentUserId = user?.id || '';
  const [facilities, setFacilities] = useState([]);
  const [team, setTeam] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [networks, setNetworks] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [sortBy, setSortBy] = useState('stage');
  const [filterCountry, setFilterCountry] = useState('all');
  const [filterState, setFilterState] = useState('all');
  const [filterNetwork, setFilterNetwork] = useState('all');
  const [filterStage, setFilterStage] = useState('all');
  const [filterLead, setFilterLead] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [showAddFacility, setShowAddFacility] = useState(false);
  const [editFacility, setEditFacility] = useState(null);
  const [showAddNetwork, setShowAddNetwork] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [flipTarget, setFlipTarget] = useState(null);
  const [dealStatusTarget, setDealStatusTarget] = useState(null);
  const [exportCopied, setExportCopied] = useState(false);

  const fetchDashboard = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    const result = await commandCenterService.getDashboardData();
    if (result.success && result.data) {
      setFacilities(result.data.facilities.map(mapApiFacility));
      setTeam(result.data.team.map((u, i) => mapTeamMember(u, i)));
      setNetworks(result.data.networks);
    }
    if (!silent) setIsLoading(false);
  }, []);

  useEffect(() => {
    void fetchDashboard();
    const interval = setInterval(() => void fetchDashboard(true), 60_000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  const countries = useMemo(
    () => [...new Set(facilities.map((f) => f.country))].sort(),
    [facilities]
  );
  const states = useMemo(() => {
    const s = facilities
      .filter((f) => filterCountry === 'all' || f.country === filterCountry)
      .map((f) => f.state);
    return [...new Set(s)].sort();
  }, [facilities, filterCountry]);
  const networkNames = useMemo(
    () => [...new Set(facilities.map((f) => f.network))].filter(Boolean).sort(),
    [facilities]
  );

  const filtered = useMemo(() => {
    let r = [...facilities];
    if (filterStatus !== 'all') r = r.filter((f) => (f.dealStatus || 'active') === filterStatus);
    if (filterCountry !== 'all') r = r.filter((f) => f.country === filterCountry);
    if (filterState !== 'all') r = r.filter((f) => f.state === filterState);
    if (filterNetwork !== 'all') r = r.filter((f) => f.network === filterNetwork);
    if (filterStage !== 'all') r = r.filter((f) => f.stage === filterStage);
    if (filterLead !== 'all') r = r.filter((f) => f.lead === filterLead);
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      r = r.filter(
        (f) =>
          f.name.toLowerCase().includes(s) ||
          f.city.toLowerCase().includes(s) ||
          f.network.toLowerCase().includes(s)
      );
    }
    r.sort((a, b) => {
      if (sortBy === 'stage') return STAGE_MAP[b.stage].idx - STAGE_MAP[a.stage].idx;
      if (sortBy === 'value') return b.dealValue - a.dealValue;
      if (sortBy === 'health') return calcDynamicHealth(b) - calcDynamicHealth(a);
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'geography')
        return a.country.localeCompare(b.country) || a.state.localeCompare(b.state);
      if (sortBy === 'velocity') return getVelocityStatus(b).ratio - getVelocityStatus(a).ratio;
      return 0;
    });
    return r;
  }, [
    facilities,
    filterCountry,
    filterState,
    filterNetwork,
    filterStage,
    filterLead,
    filterStatus,
    searchTerm,
    sortBy,
  ]);

  // Metrics (exclude lost)
  const activeFacs = facilities.filter((f) => f.dealStatus !== 'lost');
  const totalPipeline = activeFacs.reduce((s, f) => s + f.dealValue, 0);
  const weightedPipeline = activeFacs.reduce(
    (s, f) => s + f.dealValue * (STAGE_MAP[f.stage]?.prob || 0),
    0
  );
  const contractedStages = ['signed', 'onboarding', 'commercial'];
  const contractedARR = activeFacs
    .filter((f) => contractedStages.includes(f.stage))
    .reduce((s, f) => s + f.dealValue, 0);
  const activeDeals = activeFacs.filter(
    (f) => !['commercial', 'identified'].includes(f.stage) && f.dealStatus !== 'on_hold'
  ).length;
  const staleCount = activeFacs.filter(
    (f) => getVelocityStatus(f).ratio > 1.5 && f.stage !== 'commercial'
  ).length;

  const updateFacilityLocal = (id, updater) => {
    setFacilities((prev) =>
      prev.map((f) =>
        f.id === id ? (typeof updater === 'function' ? updater(f) : { ...f, ...updater }) : f
      )
    );
    setSelectedFacility((prev) =>
      prev?.id === id
        ? typeof updater === 'function'
          ? updater(prev)
          : { ...prev, ...updater }
        : prev
    );
  };

  const handleFlipToCommercial = async (id, startDate) => {
    updateFacilityLocal(id, (f) => ({
      ...f,
      stage: 'commercial',
      milestones: { ...f.milestones, commercial: startDate },
      projectedStart: startDate,
    }));
    await commandCenterService.updateFacility(id, {
      stage: 'commercial',
      projectedStart: startDate,
    });
    await commandCenterService.upsertMilestone(id, 'commercial', startDate);
    void fetchDashboard(true);
  };
  const handleStageChange = async (id, newKey) => {
    const today = todayStr();
    updateFacilityLocal(id, (f) => ({
      ...f,
      stage: newKey,
      milestones: { ...f.milestones, [newKey]: f.milestones[newKey] || today },
      lastActivity: today,
    }));
    await commandCenterService.updateFacilityStage(id, newKey);
    await commandCenterService.upsertMilestone(id, newKey, today);
    void fetchDashboard(true);
  };
  const handleDelete = async (id) => {
    setFacilities((prev) => prev.filter((f) => f.id !== id));
    setSelectedFacility(null);
    await commandCenterService.deleteFacility(id);
  };
  const handleUpdateAdvocates = async (id, adv) => {
    updateFacilityLocal(id, { advocates: adv });
    await commandCenterService.updateAdvocates(id, adv);
    void fetchDashboard(true);
  };
  const handleUpdateField = async (id, field, value) => {
    // Map API field names to local state field names where they differ
    const localField = field === 'leadId' ? 'lead' : field;
    updateFacilityLocal(id, { [localField]: value });
    // Route fields with dedicated endpoints
    if (field === 'notes') {
      await commandCenterService.updateFacilityNotes(id, value);
    } else {
      await commandCenterService.updateFacility(id, { [field]: value });
    }
  };
  const handleAddActivity = async (id, entry) => {
    updateFacilityLocal(id, (f) => ({ ...f, activityLog: [...(f.activityLog || []), entry] }));
    await commandCenterService.createActivityLog(id, { activityText: entry.text });
    void fetchDashboard(true);
  };
  const handleDealStatus = async (id, status, reason) => {
    updateFacilityLocal(id, (f) => ({ ...f, dealStatus: status }));
    await commandCenterService.updateDealStatus(id, { status, reason });
    void fetchDashboard(true);
  };

  const handleExport = () => {
    const text = generateExport(facilities, team);
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setExportCopied(true);
        setTimeout(() => setExportCopied(false), 2000);
      })
      .catch(() => {});
  };

  const selectStyle = {
    background: '#0d1520',
    border: '1px solid #1e2d3d',
    borderRadius: 4,
    color: '#94a3b8',
    padding: '6px 10px',
    fontSize: 12,
    cursor: 'pointer',
    outline: 'none',
    minWidth: 90,
  };

  if (isLoading) {
    return (
      <div
        style={{
          fontFamily: "'DM Sans','SF Pro Display',-apple-system,sans-serif",
          background: '#080d14',
          color: '#64748b',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
        }}
      >
        Loading Command Center...
      </div>
    );
  }

  return (
    <div
      style={{
        fontFamily: "'DM Sans','SF Pro Display',-apple-system,sans-serif",
        background: '#080d14',
        color: '#e2e8f0',
        minHeight: '100vh',
        width: '100%',
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,500;0,9..40,700&family=JetBrains+Mono:wght@400;600&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        ::-webkit-scrollbar{width:6px} ::-webkit-scrollbar-track{background:#0a1018} ::-webkit-scrollbar-thumb{background:#1e2d3d;border-radius:3px}
        option{background:#0d1520;color:#94a3b8} input::placeholder,textarea::placeholder{color:#334155}
      `}</style>

      {/* TOP BAR */}
      <div
        style={{
          padding: '16px 28px',
          borderBottom: '1px solid #1e2d3d',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(90deg,#080d14,#0d1520)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <img src={exexLogo} alt="eXeX" style={{ height: 28, objectFit: 'contain' }} />
          <div style={{ width: 1, height: 24, background: '#1e2d3d' }} />
          <div
            style={{
              fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
              fontSize: 14,
              color: '#06b6d4',
              fontWeight: 600,
              letterSpacing: '4px',
              textTransform: 'uppercase' as const,
            }}
          >
            COMMAND CENTER
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {team.map((t) => (
            <div
              key={t.id}
              title={`${t.name} — ${t.role}`}
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: `${t.color}20`,
                border: `1.5px solid ${t.color}50`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 700,
                color: t.color,
                cursor: 'default',
              }}
            >
              {t.initials}
            </div>
          ))}
          {canEdit && (
            <button
              onClick={() => navigate('/members')}
              title="Manage Team"
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'transparent',
                border: '1.5px dashed #1e2d3d',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                color: '#475569',
                cursor: 'pointer',
              }}
              onMouseOver={(e) => {
                e.target.style.borderColor = '#06b6d460';
                e.target.style.color = '#06b6d4';
              }}
              onMouseOut={(e) => {
                e.target.style.borderColor = '#1e2d3d';
                e.target.style.color = '#475569';
              }}
            >
              +
            </button>
          )}
          <div style={{ width: 1, height: 24, background: '#1e2d3d', margin: '0 4px' }} />
          <button
            onClick={handleExport}
            style={{
              padding: '8px 14px',
              borderRadius: 6,
              border: '1px solid #1e2d3d',
              background: 'transparent',
              color: exportCopied ? '#10b981' : '#64748b',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            {exportCopied ? '✓ Copied!' : 'Export'}
          </button>
          {canEdit && (
            <button
              onClick={() => setShowAddNetwork(true)}
              style={{
                padding: '8px 18px',
                borderRadius: 6,
                border: '1px solid #00ffc840',
                background: 'linear-gradient(135deg,#00ffc815,#00ffc808)',
                color: '#00ffc8',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                letterSpacing: '0.5px',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.boxShadow = '0 0 16px #00ffc815';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <span style={{ fontSize: 15 }}>+</span> Add Network
            </button>
          )}
          {canEdit && networks.length > 0 && (
            <button
              onClick={() => setShowAddFacility(true)}
              style={{
                padding: '8px 18px',
                borderRadius: 6,
                border: '1px solid #00ffc840',
                background: 'linear-gradient(135deg,#00ffc815,#00ffc808)',
                color: '#00ffc8',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                letterSpacing: '0.5px',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.boxShadow = '0 0 16px #00ffc815';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <span style={{ fontSize: 15 }}>+</span> Add Facility
            </button>
          )}
        </div>
      </div>

      {/* METRICS */}
      <div style={{ padding: '20px 28px', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <MetricCard
          label="Total Pipeline"
          value={formatCurrency(totalPipeline)}
          sub={`${activeFacs.length} facilities`}
        />
        <MetricCard
          label="Weighted Pipeline"
          value={formatCurrency(weightedPipeline)}
          sub="Custom probability"
          accent="#06b6d4"
        />
        <MetricCard
          label="Contracted ARR"
          value={formatCurrency(contractedARR)}
          sub={`${activeFacs.filter((f) => contractedStages.includes(f.stage)).length} contracted`}
          accent="#10b981"
        />
        <MetricCard label="Active Deals" value={activeDeals} sub="In progress" accent="#f59e0b" />
        <MetricCard
          label="Stale Deals"
          value={staleCount}
          sub={staleCount > 0 ? 'Need attention' : 'All healthy'}
          accent={staleCount > 0 ? '#ef4444' : '#00ffc8'}
        />
      </div>

      {/* CHARTS ROW */}
      <div style={{ padding: '0 28px 20px', display: 'flex', gap: 12 }}>
        <div style={{ flex: 2 }}>
          <RevenueForecastChart facilities={activeFacs} />
        </div>
        <div style={{ flex: 1 }}>
          <PipelineFunnel facilities={filtered} />
        </div>
      </div>

      {/* NETWORK PENETRATION */}
      <div style={{ padding: '0 28px 20px', display: 'flex', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <NetworkPenetrationPanel
            facilities={activeFacs}
            networks={networks}
            canEdit={canEdit}
            onSelectNetwork={
              canEdit
                ? (n) => {
                    setSelectedFacility(null);
                    setSelectedNetwork(n);
                  }
                : undefined
            }
          />
        </div>
      </div>

      {/* FILTERS */}
      <div
        style={{
          padding: '12px 28px',
          borderTop: '1px solid #1e2d3d',
          borderBottom: '1px solid #1e2d3d',
          background: '#0a1018',
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            ...selectStyle,
            minWidth: 150,
            borderColor: searchTerm ? '#00ffc840' : '#1e2d3d',
          }}
        />
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{
            ...selectStyle,
            borderColor: filterStatus !== 'active' ? '#f59e0b40' : '#1e2d3d',
          }}
        >
          <option value="active">Active</option>
          <option value="on_hold">On Hold</option>
          <option value="lost">Lost</option>
          <option value="all">All Deals</option>
        </select>
        <div style={{ width: 1, height: 24, background: '#1e2d3d' }} />
        <select
          value={filterCountry}
          onChange={(e) => {
            setFilterCountry(e.target.value);
            setFilterState('all');
          }}
          style={selectStyle}
        >
          <option value="all">All Countries</option>
          {countries.map((c) => (
            <option key={c} value={c}>
              {getCountryFlag(c)} {c}
            </option>
          ))}
        </select>
        <select
          value={filterState}
          onChange={(e) => setFilterState(e.target.value)}
          style={selectStyle}
        >
          <option value="all">All Regions</option>
          {states.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={filterNetwork}
          onChange={(e) => setFilterNetwork(e.target.value)}
          style={selectStyle}
        >
          <option value="all">All Networks</option>
          {networkNames.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <select
          value={filterStage}
          onChange={(e) => setFilterStage(e.target.value)}
          style={selectStyle}
        >
          <option value="all">All Stages</option>
          {STAGES.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>
        <select
          value={filterLead}
          onChange={(e) => setFilterLead(e.target.value)}
          style={selectStyle}
        >
          <option value="all">All Leads</option>
          {team.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <div style={{ width: 1, height: 24, background: '#1e2d3d' }} />
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={selectStyle}>
          <option value="stage">Sort: Stage</option>
          <option value="value">Sort: Value</option>
          <option value="health">Sort: Health</option>
          <option value="velocity">Sort: Velocity</option>
          <option value="name">Sort: Name</option>
          <option value="geography">Sort: Geography</option>
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 2 }}>
          {['table', 'kanban'].map((mode) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              style={{
                background: viewMode === mode ? '#1e2d3d' : 'transparent',
                border: '1px solid #1e2d3d',
                borderRadius: 4,
                color: viewMode === mode ? '#e2e8f0' : '#475569',
                padding: '5px 12px',
                fontSize: 11,
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 600,
              }}
            >
              {mode === 'table' ? '▤ Table' : '▥ Board'}
            </button>
          ))}
        </div>
      </div>

      {/* TABLE / KANBAN */}
      <div style={{ padding: '20px 28px' }}>
        {viewMode === 'table' ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #1e2d3d' }}>
                  {[
                    'Facility',
                    'Network',
                    'Location',
                    'Stage',
                    'Progress',
                    'Value',
                    'Lead',
                    'Advocates',
                    'Health',
                    'Velocity',
                    'Next Action',
                    '',
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: '10px 10px',
                        textAlign: 'left',
                        fontSize: 10,
                        color: '#475569',
                        textTransform: 'uppercase',
                        letterSpacing: '0.8px',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((f) => {
                  const isOverdue =
                    f.nextActionDate && new Date(f.nextActionDate) < new Date(todayStr());
                  const dynH = calcDynamicHealth(f);
                  const vel = getVelocityStatus(f);
                  const isInactive = f.dealStatus === 'lost' || f.dealStatus === 'on_hold';
                  return (
                    <tr
                      key={f.id}
                      onClick={() => {
                        setSelectedNetwork(null);
                        setSelectedFacility(f);
                      }}
                      style={{
                        borderBottom: '1px solid #1e2d3d10',
                        cursor: 'pointer',
                        transition: 'background 0.15s',
                        opacity: isInactive ? 0.5 : 1,
                      }}
                      onMouseOver={(e) => (e.currentTarget.style.background = '#0d152080')}
                      onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <td
                        style={{
                          padding: '12px 10px',
                          fontWeight: 600,
                          color: '#e2e8f0',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {f.name}
                        {isInactive && <DealStatusBadge status={f.dealStatus} />}
                      </td>
                      <td style={{ padding: '12px 10px', color: '#94a3b8', fontSize: 12 }}>
                        {f.network}
                      </td>
                      <td
                        style={{
                          padding: '12px 10px',
                          color: '#64748b',
                          fontSize: 12,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {getCountryFlag(f.country)} {f.city}, {f.state}
                      </td>
                      <td style={{ padding: '12px 10px' }}>
                        <StageBadge stage={f.stage} />
                      </td>
                      <td style={{ padding: '12px 10px', minWidth: 80 }}>
                        <ProgressBar stage={f.stage} />
                      </td>
                      <td
                        style={{
                          padding: '12px 10px',
                          fontWeight: 600,
                          fontFamily: "'JetBrains Mono',monospace",
                          fontSize: 12,
                          color: '#e2e8f0',
                        }}
                      >
                        {formatCurrency(f.dealValue)}
                      </td>
                      <td style={{ padding: '12px 10px' }}>
                        <TeamBadge memberId={f.lead} team={team} />
                      </td>
                      <td style={{ padding: '12px 10px' }}>
                        {(() => {
                          const adv = f.advocates || [];
                          const r =
                            f.surgeons > 0 ? Math.round((adv.length / f.surgeons) * 100) : 0;
                          const rc = r >= 10 ? '#00ffc8' : r >= 5 ? '#f59e0b' : '#475569';
                          return (
                            <span
                              style={{
                                fontSize: 11,
                                color: adv.length > 0 ? '#e2e8f0' : '#334155',
                                fontFamily: "'JetBrains Mono',monospace",
                              }}
                            >
                              {adv.length}
                              <span style={{ color: '#334155' }}>/{f.surgeons}</span>
                            </span>
                          );
                        })()}
                      </td>
                      <td style={{ padding: '12px 10px' }}>
                        <HealthIndicator value={dynH} />
                      </td>
                      <td style={{ padding: '12px 10px' }}>
                        <span style={{ fontSize: 10, color: vel.color, fontWeight: 600 }}>
                          {vel.label}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: '12px 10px',
                          fontSize: 11,
                          color: isOverdue ? '#ef4444' : '#94a3b8',
                          maxWidth: 160,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {isOverdue && '⚠ '}
                        {f.nextAction}
                      </td>
                      <td style={{ padding: '12px 10px', textAlign: 'center' }}>
                        {canEdit && f.stage !== 'commercial' && f.dealStatus === 'active' && (
                          <button
                            title="Flip"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFlipTarget(f);
                            }}
                            style={{
                              background: 'none',
                              border: '1px solid #00ffc830',
                              borderRadius: 4,
                              color: '#00ffc8',
                              cursor: 'pointer',
                              padding: '3px 8px',
                              fontSize: 10,
                              fontWeight: 700,
                              opacity: 0.6,
                            }}
                            onMouseOver={(e) => (e.target.style.opacity = 1)}
                            onMouseOut={(e) => (e.target.style.opacity = 0.6)}
                          >
                            ✦
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div style={{ padding: 40, textAlign: 'center', color: '#475569', fontSize: 13 }}>
                No facilities match filters.
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 12 }}>
            {STAGES.filter((s) => filtered.some((f) => f.stage === s.key)).map((stage) => {
              const sf = filtered.filter((f) => f.stage === stage.key);
              return (
                <div key={stage.key} style={{ minWidth: 240, maxWidth: 260, flexShrink: 0 }}>
                  <div
                    style={{
                      padding: '10px 14px',
                      borderRadius: '6px 6px 0 0',
                      background: `${stage.color}12`,
                      borderTop: `2px solid ${stage.color}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        color: stage.color,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {stage.label}
                    </span>
                    <span style={{ fontSize: 11, color: '#475569' }}>{sf.length}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 6 }}>
                    {sf.map((f) => {
                      const vel = getVelocityStatus(f);
                      return (
                        <div
                          key={f.id}
                          onClick={() => {
                            setSelectedNetwork(null);
                            setSelectedFacility(f);
                          }}
                          style={{
                            background: '#0d1520',
                            border: `1px solid ${vel.ratio > 1.5 ? vel.color + '30' : '#1e2d3d'}`,
                            borderRadius: 6,
                            padding: '12px 14px',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                            opacity: f.dealStatus !== 'active' ? 0.5 : 1,
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.borderColor = `${stage.color}40`;
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.borderColor =
                              vel.ratio > 1.5 ? vel.color + '30' : '#1e2d3d';
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: 4,
                            }}
                          >
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>
                              {f.name}
                            </div>
                            <DealStatusBadge status={f.dealStatus} />
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
                            {getCountryFlag(f.country)} {f.city} · {f.network}
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <span
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: '#e2e8f0',
                                fontFamily: "'JetBrains Mono',monospace",
                              }}
                            >
                              {formatCurrency(f.dealValue)}
                            </span>
                            <HealthIndicator value={calcDynamicHealth(f)} />
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginTop: 8,
                            }}
                          >
                            <TeamBadge memberId={f.lead} team={team} />
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              <span style={{ fontSize: 9, color: vel.color, fontWeight: 600 }}>
                                {vel.label}
                              </span>
                              {canEdit && f.stage !== 'commercial' && f.dealStatus === 'active' && (
                                <button
                                  title="Flip to Commercial"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFlipTarget(f);
                                  }}
                                  style={{
                                    background: 'none',
                                    border: '1px solid #00ffc830',
                                    borderRadius: 4,
                                    color: '#00ffc8',
                                    cursor: 'pointer',
                                    padding: '2px 7px',
                                    fontSize: 9,
                                    fontWeight: 700,
                                    opacity: 0.5,
                                    transition: 'opacity 0.15s',
                                  }}
                                  onMouseOver={(e) => (e.target.style.opacity = 1)}
                                  onMouseOut={(e) => (e.target.style.opacity = 0.5)}
                                >
                                  ✦
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* FOOTER */}
      <div
        style={{
          padding: '16px 28px',
          borderTop: '1px solid #1e2d3d',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 11,
          color: '#334155',
        }}
      >
        <span>
          eXeX Command Center · {filtered.length} of {facilities.length} facilities · {team.length}{' '}
          team
        </span>
        <span>Weights: Custom probability · Health: Dynamic (velocity-adjusted)</span>
      </div>

      {/* PANELS & MODALS */}
      {selectedFacility && (
        <>
          <div
            onClick={() => setSelectedFacility(null)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 999,
            }}
          />
          <FacilityDetailPanel
            facility={selectedFacility}
            onClose={() => setSelectedFacility(null)}
            onFlipToCommercial={(f) => {
              setSelectedFacility(null);
              setFlipTarget(f);
            }}
            onStageChange={handleStageChange}
            onUpdateAdvocates={handleUpdateAdvocates}
            onUpdateField={handleUpdateField}
            onAddActivity={handleAddActivity}
            onSetDealStatus={(f) => {
              setSelectedFacility(null);
              setDealStatusTarget(f);
            }}
            onDelete={handleDelete}
            onEdit={(f) => {
              setSelectedFacility(null);
              setEditFacility(f);
            }}
            team={team}
            canEdit={canEdit}
            currentUserId={currentUserId}
          />
        </>
      )}
      {selectedNetwork && (
        <>
          <div
            onClick={() => setSelectedNetwork(null)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 999,
            }}
          />
          <NetworkDetailPanel
            network={selectedNetwork}
            facilities={facilities}
            onClose={() => setSelectedNetwork(null)}
            onUpdate={async (id, newName, totalFacilities) => {
              await commandCenterService.upsertNetwork({ name: newName, totalFacilities });
              setSelectedNetwork(null);
              void fetchDashboard(true);
            }}
            onDelete={async (_id, networkName) => {
              await commandCenterService.deleteNetwork(networkName);
              setSelectedNetwork(null);
              void fetchDashboard(true);
            }}
          />
        </>
      )}
      {showAddNetwork && (
        <AddNetworkModal
          onClose={() => setShowAddNetwork(false)}
          onSave={async (networkName, totalFacilities) => {
            await commandCenterService.upsertNetwork({ name: networkName, totalFacilities });
            void fetchDashboard(true);
          }}
        />
      )}
      {showAddFacility && (
        <AddFacilityModal
          onClose={() => setShowAddFacility(false)}
          onSave={async (f) => {
            const result = await commandCenterService.createFacility(f);
            if (result.success) void fetchDashboard(true);
          }}
          team={team}
          networks={networks}
        />
      )}
      {editFacility && (
        <AddFacilityModal
          facility={editFacility}
          onClose={() => setEditFacility(null)}
          onSave={async (data) => {
            const result = await commandCenterService.updateFacility(editFacility.id, data);
            if (result.success) void fetchDashboard(true);
          }}
          team={team}
          networks={networks}
        />
      )}
      {flipTarget && (
        <FlipToCommercialModal
          facility={flipTarget}
          onClose={() => setFlipTarget(null)}
          onConfirm={handleFlipToCommercial}
        />
      )}
      {dealStatusTarget && (
        <DealStatusModal
          facility={dealStatusTarget}
          onClose={() => setDealStatusTarget(null)}
          onConfirm={handleDealStatus}
        />
      )}
    </div>
  );
}
